import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { db, auth, googleProvider } from "../firebase";
import { ref, onValue, update, set, remove } from "firebase/database";
import { signInWithPopup, signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged } from "firebase/auth";
import { APP_VERSION } from "../version";
import { getAllEvents } from "./events";

/* ============================================================
   לוח שעות שבועי — אפליקציה מותקנת (PWA), עברית, RTL
   תצוגות יום/שבוע/חודש · חגים וחופשות · מזג אוויר
   עיצוב: ברוטליזם קוטורי — שחור־פחם, טיפוגרפיה ענקית, אקסנט חומצתי
   ============================================================ */

const base = (uid) => `schedule/users/${uid}`;
const uid7 = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-3);

// "מתוך תמונה" — Cloudflare Worker that reads a timetable photo via Gemini
const VISION_ENDPOINT = "https://mie-schedule-vision.miller-kfir.workers.dev";

// "תזכורות" — Web Push worker (cron) + VAPID public key
const PUSH_ENDPOINT = "https://mie-schedule-push.miller-kfir.workers.dev";
const VAPID_PUBLIC = "BKj0DZMjSD6gl-EiPzjriTMmW24DkRCqQCJ5iQrlKfZTXWIJR0_6Kk5qzhpHij5I48Sz0aN8KBJ5RCHmenzwWM0";
function urlB64ToUint8(base64) {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}
const parseStartHM = (time) => {
  const m = String(time || "").match(/(\d{1,2}):(\d{2})/);
  return m ? `${pad(+m[1])}:${m[2]}` : null;
};
// reminder plan from the active timetable: [{day, hm, label}]
function buildReminderPlan(active, rows, mode) {
  const entries = [];
  for (const r of rows) {
    const hm = parseStartHM(r.time);
    if (!hm) continue;
    for (const d of DAYS) {
      const c = active && active.cells && active.cells[r.id] && active.cells[r.id][d.key];
      if (c && c.text) entries.push({ day: Number(d.key), hm, label: c.text });
    }
  }
  if (mode === "first") {
    const firstByDay = {};
    for (const e of entries) if (!firstByDay[e.day] || e.hm < firstByDay[e.day].hm) firstByDay[e.day] = e;
    return Object.values(firstByDay);
  }
  return entries;
}
// date-based reminders for homework / exams — one push the EVENING BEFORE the due
// date, aggregating all children's items for that day. Shape: {date, hm, label}
function buildEventPlan(boards, hm) {
  const todayY = ymd(new Date());
  const byDate = {}; // reminderDate(YYYY-MM-DD) -> [labels]
  for (const b of boards || []) {
    const evs = Array.isArray(b.events) ? b.events : [];
    const who = String(b.name || "").trim().split(/\s+/)[0] || "";
    for (const e of evs) {
      if (!e || !/^\d{4}-\d{2}-\d{2}$/.test(e.date)) continue;
      const rd = new Date(e.date + "T00:00");
      rd.setDate(rd.getDate() - 1);
      const remindDate = ymd(rd);
      if (remindDate < todayY) continue; // never fires in the past
      const subject = String(e.text || "").split(" — ")[0].trim() || "מטלה";
      const kind = e.type === "exam" ? "מבחן" : "מטלה";
      const label = `${who ? who + " · " : ""}${kind}: ${subject}`;
      (byDate[remindDate] = byDate[remindDate] || []).push(label);
    }
  }
  return Object.entries(byDate).map(([date, labels]) => ({
    date,
    hm: hm || "18:00",
    kind: "event",
    label: "מחר — " + labels.slice(0, 4).join(" · ") + (labels.length > 4 ? ` ועוד ${labels.length - 4}` : ""),
  }));
}
const pushSupported = () =>
  typeof navigator !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

// "משיכה ממשו"ב" — Cloudflare Worker that proxies Mashov login + timetable
const MASHOV_ENDPOINT = "https://mie-schedule-mashov.miller-kfir.workers.dev";
const NEWS_ENDPOINT = "https://mie-schedule-news.miller-kfir.workers.dev";
// Google Calendar — read upcoming meetings. We use the GIS *code* flow: consent
// once (popup), then a long-lived refresh token (kept by worker-gcal, which holds
// the client secret) mints fresh access tokens silently. This is the only way to
// keep calendar access alive in an installed iOS PWA, where the implicit-token
// silent refresh is blocked by ITP and pops a window on every open.
const GCAL_CLIENT_ID = "594409333755-mf3l5pveqd0fm3nfsop7561o03tc3a70.apps.googleusercontent.com";
const GCAL_ENDPOINT = "https://mie-schedule-gcal.miller-kfir.workers.dev";
// persist the short-lived access token (valid ~1h) so reopening within the hour
// needs no network round-trip.
function gcalSaveToken(t) {
  try { localStorage.setItem("gcal_tok", JSON.stringify({ t, at: Date.now() })); localStorage.setItem("gcal_connected", "1"); } catch {}
}
function gcalReadToken() {
  try {
    const o = JSON.parse(localStorage.getItem("gcal_tok") || "null");
    if (o && o.t && Date.now() - o.at < 55 * 60000) return o.t;
  } catch {}
  return null;
}
function gcalClearToken() {
  try { localStorage.removeItem("gcal_tok"); } catch {}
}
// the refresh token — the durable "stay connected" credential
function gcalSaveRefresh(rt) { try { if (rt) localStorage.setItem("gcal_rt", rt); } catch {} }
function gcalReadRefresh() { try { return localStorage.getItem("gcal_rt") || null; } catch { return null; } }
function gcalClearRefresh() { try { localStorage.removeItem("gcal_rt"); localStorage.removeItem("gcal_connected"); } catch {} }
// A stable per-install device id so Mashov recognizes this device on every login
// (instead of emailing "login from a new device" each time). Created once, reused.
function deviceId() {
  try {
    let id = localStorage.getItem("mie_device_id");
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : "mie-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
      localStorage.setItem("mie_device_id", id);
    }
    return id;
  } catch {
    return "mie-fallback";
  }
}
function loadGis() {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.google && window.google.accounts && window.google.accounts.oauth2) return resolve();
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("gis load failed"));
    document.head.appendChild(s);
  });
}

// {name, rows:[{title,time}], entries:[{row,day,text}]} (from vision or Mashov) -> {rows, cells}
function parsedToRowsCells(out) {
  const srcRows =
    Array.isArray(out.rows) && out.rows.length
      ? out.rows
      : [1, 2, 3, 4, 5, 6].map((n) => ({ title: String(n), time: "" }));
  const rows = srcRows.map((r) => ({ id: uid7(), title: String(r.title || "").trim(), time: String(r.time || "").trim() }));
  const colorMap = {};
  let ci = 0;
  const colorFor = (t) => (colorMap[t] || (colorMap[t] = SWATCHES[ci++ % SWATCHES.length]));
  const cells = {};
  (out.entries || []).forEach((e) => {
    const idx = Number(e.row) - 1;
    const day = String(e.day);
    const text = String(e.text || "").trim();
    if (idx < 0 || idx >= rows.length || !/^[0-5]$/.test(day) || !text) return;
    const rid = rows[idx].id;
    const teacher = String(e.teacher || "").trim();
    (cells[rid] = cells[rid] || {})[day] = { text, color: colorFor(text), ...(teacher ? { teacher } : {}) };
  });
  return { rows, cells };
}

/* ---------- menu line icons (clean neon-line SVGs) ---------- */
const svgProps = {
  width: 19, height: 19, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
  strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round",
};
const IconRefresh = (p) => (
  <svg {...svgProps} {...p}>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const IconBell = (p) => (
  <svg {...svgProps} {...p}>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const IconInfo = (p) => (
  <svg {...svgProps} {...p}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
const IconSend = (p) => (
  <svg {...svgProps} {...p}>
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const IconLogout = (p) => (
  <svg {...svgProps} {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
const IconPlus = (p) => (
  <svg {...svgProps} {...p}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconDownload = (p) => (
  <svg {...svgProps} {...p}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// downscale a photo to keep the upload small and OCR sharp -> {mimeType, data(base64)}
function fileToBase64Downscaled(file, maxDim = 1280) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      resolve({ mimeType: "image/jpeg", data: dataUrl.split(",")[1] });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("image load failed"));
    };
    img.src = url;
  });
}

const DAYS = [
  { key: "0", short: "א׳", name: "ראשון" },
  { key: "1", short: "ב׳", name: "שני" },
  { key: "2", short: "ג׳", name: "שלישי" },
  { key: "3", short: "ד׳", name: "רביעי" },
  { key: "4", short: "ה׳", name: "חמישי" },
  { key: "5", short: "ו׳", name: "שישי" },
];
const HE_DAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
const HE_DAYS_SHORT = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];
const HE_MONTHS = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

// ברירת מחדל — לפי לוח הזמנים שסופק (9 שיעורים)
const DEFAULT_ROWS = () =>
  [
    { title: "1", time: "08:00–08:50" },
    { title: "2", time: "08:50–09:40" },
    { title: "3", time: "10:15–11:00" },
    { title: "4", time: "11:00–11:45" },
    { title: "5", time: "12:10–12:45" },
    { title: "6", time: "12:45–13:30" },
    { title: "7", time: "13:30–14:15" },
    { title: "8", time: "14:15–15:00" },
    { title: "9", time: "15:00–15:45" },
  ].map((r) => ({ id: uid7(), ...r }));

// פלטת קוטור
const SWATCHES = [
  "#D7FF3E", "#FF2E63", "#2C6BFF", "#00E0C7", "#FF7A00",
  "#B36BFF", "#7A1F2B", "#E8C7A8", "#F2ECE0", "#5A5A5A",
];

function textOn(hex) {
  if (!hex) return "#f2ece0";
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? "#0a0a0a" : "#f7f3ea";
}

/* ---------- date utils ---------- */
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtDMY = (d) => `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
const fmtDM = (d) => `${d.getDate()}.${d.getMonth() + 1}`;
const atMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfWeek = (d) => addDays(d, -d.getDay()); // Sunday
const sameDay = (a, b) => ymd(a) === ymd(b);
// "08:00–08:50" -> {s:480, e:530} minutes
function parseRange(str) {
  const m = String(str || "").match(/(\d{1,2}):(\d{2})\D+(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return { s: +m[1] * 60 + +m[2], e: +m[3] * 60 + +m[4] };
}
const isRainy = (c) => (c >= 51 && c <= 67) || (c >= 80 && c <= 82) || (c >= 95 && c <= 99);
// PE / sports lesson -> remind to bring sports clothes
const SPORT_RE = /(חינוך גופני|ח['.׳]?\s*גופני|חנ["״]?ג|ספורט|התעמלות)/;
// "08:00–08:50" -> ["08:00","08:50"]
function splitTime(time) {
  const parts = String(time || "").split(/[–-]/);
  if (parts.length >= 2) return [parts[0].trim(), parts.slice(1).join("-").trim()];
  return [String(time || "").trim(), ""];
}
// number -> Hebrew gematria letters with geresh/gershayim (e.g. 9->ט׳, 15->ט״ו, 787->תשפ״ז)
function heNum(n) {
  const ones = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
  const tens = ["", "י", "כ", "ל", "מ", "נ", "ס", "ע", "פ", "צ"];
  const huns = ["", "ק", "ר", "ש", "ת", "תק", "תר", "תש", "תת", "תתק"];
  n %= 1000;
  let s = huns[Math.floor(n / 100)];
  n %= 100;
  if (n === 15) s += "טו";
  else if (n === 16) s += "טז";
  else s += tens[Math.floor(n / 10)] + ones[n % 10];
  return s;
}
function heNumFmt(n) {
  const s = heNum(n);
  if (s.length <= 1) return s + "׳";
  return s.slice(0, -1) + "״" + s.slice(-1);
}
// Hebrew (Jewish-calendar) date in Hebrew letters, e.g. "ט׳ בתשרי תשפ״ז"
function hebrewDate(d) {
  try {
    const parts = new Intl.DateTimeFormat("he-u-ca-hebrew", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).formatToParts(d);
    return parts
      .map((p) => {
        if (p.type === "day") return heNumFmt(+p.value);
        if (p.type === "year") return heNumFmt(+p.value % 1000);
        return p.value;
      })
      .join("");
  } catch {
    return "";
  }
}

/* ---------- holidays (Hebcal) + Israeli school breaks ---------- */
function hebcalUrl(year) {
  return `https://www.hebcal.com/hebcal?v=1&cfg=json&year=${year}&month=x&maj=on&min=on&mod=on&nx=off&mf=on&ss=off&c=off&geo=none&lg=h`;
}
async function fetchHolidaysYear(year) {
  const key = `sched_hol_${year}`;
  try {
    const c = JSON.parse(localStorage.getItem(key) || "null");
    if (c && c.ts && Date.now() - c.ts < 45 * 864e5 && c.map) return c.map;
  } catch {}
  const res = await fetch(hebcalUrl(year));
  const j = await res.json();
  const map = {};
  (j.items || []).forEach((it) => {
    if (!it.date) return;
    if (!["holiday", "modern", "fast"].includes(it.category)) return;
    const day = String(it.date).slice(0, 10);
    const title = it.hebrew || it.title;
    (map[day] = map[day] || []).push(title);
  });
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), map }));
  } catch {}
  return map;
}
// חופש גדול (קירוב) — יולי–אוגוסט
function staticBreak(d) {
  const m = d.getMonth();
  if (m === 6 || m === 7) return "חופש גדול";
  return null;
}

/* ---------- weather (Open-Meteo, no key) ---------- */
const WMO = {
  0: ["☀️", "בהיר"], 1: ["🌤️", "בהיר ברובו"], 2: ["⛅", "מעונן חלקית"], 3: ["☁️", "מעונן"],
  45: ["🌫️", "ערפל"], 48: ["🌫️", "ערפל"],
  51: ["🌦️", "טפטוף"], 53: ["🌦️", "טפטוף"], 55: ["🌦️", "טפטוף"],
  56: ["🌧️", "טפטוף קפוא"], 57: ["🌧️", "טפטוף קפוא"],
  61: ["🌧️", "גשם"], 63: ["🌧️", "גשם"], 65: ["🌧️", "גשם חזק"],
  66: ["🌧️", "גשם קפוא"], 67: ["🌧️", "גשם קפוא"],
  71: ["🌨️", "שלג"], 73: ["🌨️", "שלג"], 75: ["🌨️", "שלג כבד"], 77: ["🌨️", "גרגרי שלג"],
  80: ["🌦️", "ממטרים"], 81: ["🌦️", "ממטרים"], 82: ["⛈️", "ממטרים עזים"],
  85: ["🌨️", "ממטרי שלג"], 86: ["🌨️", "ממטרי שלג"],
  95: ["⛈️", "סופת רעמים"], 96: ["⛈️", "רעמים וברד"], 99: ["⛈️", "רעמים וברד"],
};
const wmo = (c) => WMO[c] || ["🌡️", "—"];

async function fetchWeather(lat, lon) {
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    `&current=temperature_2m,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max` +
    `&timezone=auto&forecast_days=7`;
  const res = await fetch(url);
  const j = await res.json();
  const daily = (j.daily?.time || []).map((t, i) => ({
    date: t,
    code: j.daily.weather_code[i],
    max: Math.round(j.daily.temperature_2m_max[i]),
    min: Math.round(j.daily.temperature_2m_min[i]),
    precip: Number(j.daily.precipitation_sum?.[i] ?? 0),
    pop: Number(j.daily.precipitation_probability_max?.[i] ?? 0),
  }));
  return {
    current: { temp: Math.round(j.current?.temperature_2m), code: j.current?.weather_code },
    daily,
  };
}
function getLocation() {
  const CACHE_KEY = "wxloc";
  const OK_AGE = 30 * 864e5; // reuse a granted location for 30 days → no re-prompt
  const FB_AGE = 1 * 864e5; // after a denial, retry at most once a day (not every load)
  const fallback = { lat: 32.0853, lon: 34.7818, place: "תל אביב" };
  const valid = (o) => o && Number.isFinite(o.lat) && Number.isFinite(o.lon) && Math.abs(o.lat) <= 90 && Math.abs(o.lon) <= 180;
  // 1) reuse a cached location so we don't call geolocation (which re-prompts) every open
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    if (valid(c) && Date.now() - (c.t || 0) < (c.fb ? FB_AGE : OK_AGE)) {
      return Promise.resolve({ lat: c.lat, lon: c.lon, place: c.place || "המיקום שלך" });
    }
  } catch {}
  return new Promise((resolve) => {
    const done = (loc, fb) => {
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ lat: loc.lat, lon: loc.lon, place: loc.place, t: Date.now(), fb: !!fb })); } catch {}
      resolve(loc);
    };
    if (!navigator.geolocation) return done(fallback, true);
    navigator.geolocation.getCurrentPosition(
      (p) => done({ lat: p.coords.latitude, lon: p.coords.longitude, place: "המיקום שלך" }, false),
      () => done(fallback, true),
      { timeout: 6000, maximumAge: 3600000 }
    );
  });
}

/* ---------- PWA install ---------- */
function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}
function usePwaHead() {
  useEffect(() => {
    const added = [];
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      added.push({ el, created, prev });
    };
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/schedule.webmanifest";
    document.head.appendChild(link);
    const apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    apple.href = "/schedule-icon-180.png";
    document.head.appendChild(apple);
    setMeta("name", "apple-mobile-web-app-capable", "yes");
    setMeta("name", "mobile-web-app-capable", "yes");
    setMeta("name", "apple-mobile-web-app-status-bar-style", "black-translucent");
    setMeta("name", "apple-mobile-web-app-title", "מערכת שעות");
    const themeEl = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeEl ? themeEl.getAttribute("content") : null;
    if (themeEl) themeEl.setAttribute("content", "#0a0a0a");
    const prevTitle = document.title;
    document.title = "מערכת שעות";
    return () => {
      link.remove();
      apple.remove();
      added.forEach(({ el, created, prev }) => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute("content", prev);
      });
      if (themeEl && prevTheme != null) themeEl.setAttribute("content", prevTheme);
      document.title = prevTitle;
    };
  }, []);
}
function useMedia(query) {
  const [match, setMatch] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, [query]);
  return match;
}

/* ============================================================
   Page
   ============================================================ */
// Loading splash: a chessboard of green squares that all pulse in-and-out together
// (back and forth) a couple of times, then the overlay crossfades into the content.
function Splash({ onReveal, onDone }) {
  const { cols, rows } = useMemo(() => {
    const vw = typeof window !== "undefined" ? window.innerWidth : 400;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const c = vw < 600 ? 8 : Math.min(16, Math.max(8, Math.round(vw / 90)));
    return { cols: c, rows: Math.ceil(vh / (vw / c)) + 1 };
  }, []);
  const STEP = 22; // ms between diagonal steps
  const DUR = 340; // ms per green flash
  const PAUSE = 60; // between the green wave (out) and the reveal wave (back)
  const REVEAL = 440; // ms per tile: green crest → fade away (reveals content behind)
  const maxDist = cols - 1 + (rows - 1);
  const [gone, setGone] = useState(false);
  useEffect(() => {
    // reveal starts when the return (wipe) wave begins; content is already behind.
    const wipeStart = maxDist * STEP + PAUSE;
    const t1 = setTimeout(() => { setGone(true); onReveal && onReveal(); }, wipeStart);
    const t2 = setTimeout(onDone, 2 * maxDist * STEP + PAUSE + REVEAL + 60); // after the last tile finishes revealing
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [maxDist, onReveal, onDone]);
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const dist = r + (cols - 1 - c); // top-right = 0 → bottom-left = maxDist
      const fwd = dist * STEP; // green wave: top-right → bottom-left
      const back = maxDist * STEP + PAUSE + (maxDist - dist) * STEP; // reveal wave: bottom-left → top-right
      // opaque checkerboard greens → full coverage (no peek), bright crest on the reveal wave
      const flash = (r + c) % 2 === 1 ? "#7d9b1e" : ACID;
      cells.push(
        <SplashCell
          key={`${r}-${c}`}
          style={{ "--flash": flash, "--fd": `${DUR}ms`, "--rd": `${REVEAL}ms`, "--f": `${fwd}ms`, "--b": `${back}ms` }}
        />
      );
    }
  }
  return (
    <SplashWrap $gone={gone} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }} aria-hidden="true">
      {cells}
    </SplashWrap>
  );
}

export const Schedule = () => {
  usePwaHead();
  const [authUser, setAuthUser] = useState(undefined);
  const [showSplash, setShowSplash] = useState(true);
  const [contentReady, setContentReady] = useState(true); // content renders behind the splash → visible instantly when it lifts
  const [section, setSection] = useState("kids"); // owner-only: "kids" | "me"
  const [meView, setMeView] = useState("meetings"); // "אני" sub-view: "events" | "meetings"
  const [authBusy, setAuthBusy] = useState(false);
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dbError, setDbError] = useState(false);

  const [cursor, setCursor] = useState(() => atMidnight(new Date()));
  const [manualMode, setManualMode] = useState(null); // 'day'|'week'|'month'|null(auto)
  const isPhonePortrait = useMedia("(max-width: 760px) and (orientation: portrait)");
  const mode = manualMode || (isPhonePortrait ? "day" : "week");
  const today = useMemo(() => atMidnight(new Date()), []);

  const [cellEdit, setCellEdit] = useState(null);
  const [rowsOpen, setRowsOpen] = useState(false);
  const [namePrompt, setNamePrompt] = useState(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const [weather, setWeather] = useState({ status: "idle", place: "" });
  const [gcalMeetings, setGcalMeetings] = useState(null);
  const [gcalStatus, setGcalStatus] = useState("idle"); // idle|connecting|connected|need-setup|error

  // pull the signed-in user's upcoming Google Calendar events with a granted token
  const fetchMeetings = async (token) => {
    try {
      const now = new Date().toISOString();
      // cover through the end of next month so week/month views have every day's events
      const timeMax = new Date(new Date().getFullYear(), new Date().getMonth() + 2, 1).toISOString();
      const url =
        "https://www.googleapis.com/calendar/v3/calendars/primary/events" +
        `?timeMin=${encodeURIComponent(now)}&timeMax=${encodeURIComponent(timeMax)}&maxResults=100&singleEvents=true&orderBy=startTime`;
      const r = await fetch(url, { headers: { Authorization: "Bearer " + token } });
      if (r.status === 401) { gcalClearToken(); refreshAccess(); return; } // expired → silent refresh via refresh token (no popup)
      if (r.status === 403) { setGcalStatus("need-setup"); return; }
      if (!r.ok) { setGcalStatus("error"); return; }
      const j = await r.json();
      const videoLink = (e) => {
        if (e.hangoutLink) return e.hangoutLink;
        const ep = e.conferenceData && e.conferenceData.entryPoints;
        if (Array.isArray(ep)) {
          const v = ep.find((p) => p.entryPointType === "video" && p.uri);
          if (v) return v.uri;
        }
        const m = `${e.location || ""} ${e.description || ""}`.match(/https?:\/\/[^\s"'<>]*(?:zoom\.us|meet\.google\.com|teams\.microsoft\.com|whereby\.com)[^\s"'<>]*/i);
        return m ? m[0] : "";
      };
      const items = (j.items || []).map((e) => ({
        title: e.summary || "(ללא כותרת)",
        when: (e.start && (e.start.dateTime || e.start.date)) || "",
        end: (e.end && (e.end.dateTime || e.end.date)) || "",
        location: e.location || "",
        video: videoLink(e),
        kind: e.eventType || "default",
        workLoc: (e.workingLocationProperties && e.workingLocationProperties.type) || null,
      }));
      setGcalMeetings(items);
      setGcalStatus("connected");
    } catch {
      setGcalStatus("error");
    }
  };
  // Connect a Google Calendar via Google Identity Services (independent of the app
  // login) — so you can pick ANY account (e.g. work) without changing who you're
  // signed in as. `silent` reuses an existing consent with no popup (used on load /
  // when a token expires) so the user doesn't have to reconnect every time.
  // Connect once via the GIS *code* flow (popup, user gesture): consent, get an auth
  // code, exchange it (worker-gcal) for an access token + a durable refresh token.
  const connectCalendar = async () => {
    setGcalStatus("connecting");
    try {
      await loadGis();
      const cc = window.google.accounts.oauth2.initCodeClient({
        client_id: GCAL_CLIENT_ID,
        scope: "https://www.googleapis.com/auth/calendar.readonly",
        ux_mode: "popup",
        callback: async (resp) => {
          if (!resp || !resp.code) { setGcalStatus("idle"); return; }
          try {
            const r = await fetch(`${GCAL_ENDPOINT}/exchange`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ code: resp.code }),
            });
            const j = await r.json();
            if (j && j.access_token) {
              gcalSaveToken(j.access_token);
              if (j.refresh_token) gcalSaveRefresh(j.refresh_token);
              fetchMeetings(j.access_token);
            } else { setGcalStatus("idle"); }
          } catch { setGcalStatus("idle"); }
        },
        error_callback: () => setGcalStatus("idle"),
      });
      cc.requestCode();
    } catch {
      setGcalStatus("idle");
    }
  };
  // silently mint a fresh access token from the stored refresh token — no popup.
  const refreshAccess = async () => {
    const rt = gcalReadRefresh();
    if (!rt) { setGcalStatus("idle"); return; }
    setGcalStatus("connecting");
    try {
      const r = await fetch(`${GCAL_ENDPOINT}/refresh`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      const j = await r.json();
      if (j && j.access_token) { gcalSaveToken(j.access_token); fetchMeetings(j.access_token); }
      else { if (r.status === 401) gcalClearRefresh(); setGcalStatus("idle"); } // refresh revoked → need reconnect
    } catch { setGcalStatus("idle"); }
  };
  // ⟳ in "אני" mode → reuse a valid token, else silent refresh, else full connect
  const refreshMeetings = () => {
    const tok = gcalReadToken();
    if (tok) { setGcalStatus("connecting"); fetchMeetings(tok); }
    else if (gcalReadRefresh()) refreshAccess();
    else connectCalendar();
  };
  // fully disconnect: revoke, forget the refresh token, clear the view
  const disconnectCalendar = () => {
    const tok = gcalReadToken();
    try {
      if (tok && window.google && window.google.accounts && window.google.accounts.oauth2) {
        window.google.accounts.oauth2.revoke(tok, () => {});
      }
    } catch {}
    gcalClearToken();
    gcalClearRefresh();
    setGcalMeetings(null);
    setGcalStatus("idle");
  };
  // on load: valid token → use it; else if we have a refresh token → refresh SILENTLY
  // (worker round-trip, no Google popup). Only a first-time user sees the connect UI.
  useEffect(() => {
    const tok = gcalReadToken();
    if (tok) fetchMeetings(tok);
    else if (gcalReadRefresh()) refreshAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // safety net: always reveal the content even if the splash callback never fires
  useEffect(() => {
    const t = setTimeout(() => setContentReady(true), 4500);
    return () => clearTimeout(t);
  }, []);
  const [weatherOpen, setWeatherOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [news, setNews] = useState({ status: "idle", items: [] });
  const [avatarErr, setAvatarErr] = useState(false);
  const [holidays, setHolidays] = useState({});
  const [importing, setImporting] = useState(false);
  const [mashovConfig, setMashovConfig] = useState(null); // {mode:'create'|'edit', id?} while the form is open
  const [mashovBusy, setMashovBusy] = useState(false);
  const [mashovToast, setMashovToast] = useState(null); // {ok, msg}
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [view, setView] = useState("schedule"); // schedule | homework | behavior
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const fileRef = useRef(null);
  const loadedYears = useRef(new Set());

  const uid = authUser ? authUser.uid : null;
  const isOwner = !!(authUser && authUser.email === "miller.kfir@gmail.com"); // only me sees the הילדים/אני toggle

  useEffect(() => onAuthStateChanged(auth, (u) => setAuthUser(u || null)), []);
  // complete a redirect-based Google sign-in when returning from the provider
  useEffect(() => {
    getRedirectResult(auth).catch(() => {
      alert("ההתחברות נכשלה. נסו שוב.");
    });
  }, []);

  // Android/Chrome install prompt
  useEffect(() => {
    const onBip = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!isStandalone()) setShowInstall(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  // weather (once)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cached = JSON.parse(localStorage.getItem("sched_weather") || "null");
        if (cached && Date.now() - cached.ts < 30 * 60000) {
          if (alive) setWeather({ status: "ok", place: cached.place, ...cached.data });
          return;
        }
      } catch {}
      setWeather((w) => ({ ...w, status: "loading" }));
      const loc = await getLocation();
      try {
        const wx = await fetchWeather(loc.lat, loc.lon);
        if (!alive) return;
        setWeather({ status: "ok", place: loc.place, ...wx });
        try {
          localStorage.setItem("sched_weather", JSON.stringify({ ts: Date.now(), place: loc.place, data: wx }));
        } catch {}
      } catch {
        if (alive) setWeather({ status: "err", place: "" });
      }
    })();
    return () => (alive = false);
  }, []);

  // latest news (once)
  useEffect(() => {
    let alive = true;
    setNews((n) => ({ ...n, status: "loading" }));
    (async () => {
      try {
        const r = await fetch(NEWS_ENDPOINT);
        const j = await r.json();
        if (alive) setNews({ status: "ok", items: Array.isArray(j.items) ? j.items : [] });
      } catch {
        if (alive) setNews({ status: "err", items: [] });
      }
    })();
    return () => (alive = false);
  }, []);

  // holidays for the visible range
  useEffect(() => {
    let start, end;
    if (mode === "day") {
      start = cursor;
      end = cursor;
    } else if (mode === "week") {
      start = startOfWeek(cursor);
      end = addDays(start, 6);
    } else {
      const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      start = startOfWeek(first);
      end = addDays(start, 41);
    }
    const years = new Set([start.getFullYear(), end.getFullYear()]);
    years.forEach((y) => {
      if (loadedYears.current.has(y)) return;
      loadedYears.current.add(y);
      fetchHolidaysYear(y)
        .then((map) => setHolidays((prev) => ({ ...prev, ...map })))
        .catch(() => loadedYears.current.delete(y));
    });
  }, [cursor, mode]);

  const holidaysFor = (d) => {
    const list = holidays[ymd(d)] || [];
    const brk = staticBreak(d);
    return brk ? [brk, ...list.filter((t) => t !== brk)] : list;
  };

  // live user data
  useEffect(() => {
    if (!uid) {
      setData(null);
      setLoaded(false);
      return;
    }
    setDbError(false);
    const unsub = onValue(
      ref(db, base(uid)),
      (snap) => {
        setData(snap.val() || {});
        setLoaded(true);
        setDbError(false);
      },
      (err) => {
        setLoaded(true);
        setDbError(err && err.code === "PERMISSION_DENIED" ? "permission" : "error");
      }
    );
    return () => unsub();
  }, [uid]);

  const timetables = data && data.timetables ? data.timetables : {};
  const mashovDeleted = (data && data.mashovDeleted) || {}; // childGuids the user deleted on purpose
  const customAvatar = (data && data.avatar) || null; // user-uploaded profile picture (data URL)
  const list = useMemo(
    () =>
      Object.entries(timetables)
        .map(([id, v]) => ({ id, ...v }))
        .sort((a, b) => (a.order || 0) - (b.order || 0)),
    [timetables]
  );
  const activeId =
    data && data.activeTimetableId && timetables[data.activeTimetableId]
      ? data.activeTimetableId
      : list[0]
      ? list[0].id
      : null;
  const active = activeId ? { id: activeId, ...timetables[activeId] } : null;
  const rows = active && Array.isArray(active.rows) ? active.rows : [];
  const mail = active && active.mail ? active.mail : null; // the active child's inbox
  const eventsFor = (d) =>
    active && Array.isArray(active.events) ? active.events.filter((e) => e.date === ymd(d)) : [];

  // pull the inbox PER CHILD on load — one login fetches every child's mailbox,
  // stored on each board. Throttled so opening the app doesn't hammer Mashov login.
  useEffect(() => {
    if (!uid) return;
    const mashovBoards = list.filter((t) => t.mashov && t.mashov.childGuid);
    if (!mashovBoards.length) return;
    const newest = Math.max(0, ...mashovBoards.map((t) => (t.mail && t.mail.t) || 0));
    if (Date.now() - newest < 10 * 60000) return; // at most once every 10 minutes
    const cfg = mashovBoards[0].mashov;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(MASHOV_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ semel: cfg.semel, year: cfg.year, username: cfg.username, password: cfg.password, deviceUuid: deviceId(), mailOnly: true }),
        });
        const out = await res.json().catch(() => ({}));
        const byChild = out && out.mailByChild ? out.mailByChild : null;
        if (!alive || !byChild) return;
        for (const t of mashovBoards) {
          const m = byChild[t.mashov.childGuid];
          if (!m) continue;
          await update(ref(db, `${base(uid)}/timetables/${t.id}/mail`), {
            unread: Number(m.unread) || 0,
            items: Array.isArray(m.items) ? m.items.slice(0, 20) : [],
            t: Date.now(),
          });
        }
      } catch {}
    })();
    return () => (alive = false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, list.map((t) => t.id).join(",")]);

  /* ---------- mutations ---------- */
  const setActive = (id) => update(ref(db, base(uid)), { activeTimetableId: id });
  const createTimetable = (name) => {
    const id = uid7();
    update(ref(db, `${base(uid)}/timetables`), {
      [id]: { name: name || "לוח חדש", order: Date.now(), rows: DEFAULT_ROWS(), cells: {} },
    });
    update(ref(db, base(uid)), { activeTimetableId: id });
  };
  const renameTimetable = (id, name) => update(ref(db, `${base(uid)}/timetables/${id}`), { name: name || "ללא שם" });
  const deleteTimetable = (id) => {
    if (activeId === id) {
      const next = list.find((t) => t.id !== id);
      update(ref(db, base(uid)), { activeTimetableId: next ? next.id : null });
    }
    // if this board is linked to a Mashov child, remember it was deleted on purpose
    // so a ⟳ refresh won't recreate it (only an explicit re-setup will).
    const g = timetables[id] && timetables[id].mashov && timetables[id].mashov.childGuid;
    if (g) update(ref(db, `${base(uid)}/mashovDeleted`), { [g]: true });
    remove(ref(db, `${base(uid)}/timetables/${id}`));
  };
  const setCell = (rowId, dayKey, payload) => {
    const path = `${base(uid)}/timetables/${activeId}/cells/${rowId}/${dayKey}`;
    if (!payload || (!payload.text && !payload.color)) remove(ref(db, path));
    else set(ref(db, path), payload);
  };
  const saveRows = (nextRows) => set(ref(db, `${base(uid)}/timetables/${activeId}/rows`), nextRows);

  /* ---------- profile picture ---------- */
  const avatarFileRef = useRef(null);
  const onPickAvatar = () => { if (avatarFileRef.current) avatarFileRef.current.click(); };
  const saveAvatar = async (file) => {
    if (!file) return;
    try {
      const { mimeType, data: b64 } = await fileToBase64Downscaled(file, 256);
      await set(ref(db, `${base(uid)}/avatar`), `data:${mimeType};base64,${b64}`);
      setAvatarErr(false);
    } catch {
      alert("לא הצלחתי לטעון את התמונה. נסו תמונה אחרת.");
    }
  };

  /* ---------- import from photo ---------- */
  const importTargetRef = useRef(null);
  const onPickImage = (targetId = null) => {
    importTargetRef.current = targetId; // null = new timetable, otherwise replace this timetable's data
    if (fileRef.current) fileRef.current.click();
  };
  const importFromImage = async (file, targetId) => {
    if (!file) return;
    setImporting(true);
    try {
      const { mimeType, data } = await fileToBase64Downscaled(file);
      // Gemini vision is slow/flaky — try up to 3 times before giving up
      let out = null;
      let lastErr = "";
      for (let attempt = 0; attempt < 3 && !out; attempt++) {
        if (attempt > 0) await new Promise((s) => setTimeout(s, 1200));
        try {
          const res = await fetch(VISION_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ image: data, mimeType }),
          });
          if (res.ok) { out = await res.json(); break; }
          lastErr = "vision-" + res.status;
        } catch (e) {
          lastErr = e && e.message ? e.message : String(e);
        }
      }
      if (!out) throw new Error(lastErr || "vision-failed");
      const { rows, cells } = parsedToRowsCells(out);

      if (targetId && timetables[targetId]) {
        // replace the existing timetable's schedule (keep its name + order)
        await update(ref(db, `${base(uid)}/timetables/${targetId}`), { rows, cells });
        await update(ref(db, base(uid)), { activeTimetableId: targetId });
      } else {
        const id = uid7();
        await update(ref(db, `${base(uid)}/timetables`), {
          [id]: { name: (out.name || "").trim() || "מהתמונה", order: Date.now(), rows, cells },
        });
        await update(ref(db, base(uid)), { activeTimetableId: id });
      }
    } catch (e) {
      alert(
        "לא הצלחתי לקרוא את התמונה. ודאו שהתמונה ברורה ושמערכת ׳מתוך תמונה׳ הופעלה (פריסת ה-worker).\n" +
          "פרטים: " + (e && e.message ? e.message : e)
      );
    } finally {
      setImporting(false);
    }
  };

  /* ---------- Mashov (משו"ב) ---------- */
  const fetchMashov = async (cfg) => {
    const res = await fetch(MASHOV_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ semel: cfg.semel, year: cfg.year, username: cfg.username, password: cfg.password, childGuid: cfg.childGuid, deviceUuid: deviceId() }),
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out && out.error === "login failed" ? "שם משתמש / סיסמה / מוסד שגויים" : (out && out.error) || "mashov-" + res.status);
    return out;
  };
  // parent with multiple children -> one board per child, from a single worker
  // response (the worker logs in once and fetches all children — no repeated logins)
  const saveBoardsFromMulti = async (cfg, out, opts = {}) => {
    const respectDeleted = opts.respectDeleted !== false; // ⟳ refresh respects deletions
    const boards = Array.isArray(out.boards) ? out.boards : [];
    const failed = Array.isArray(out.failed) ? out.failed.slice() : [];
    let firstId = null;
    let lessons = 0;
    let events = 0;
    let ok = 0;
    for (let i = 0; i < boards.length; i++) {
      const b = boards[i];
      // a board the user deleted on purpose is not recreated by a refresh — only
      // an explicit re-setup (which passes respectDeleted:false) brings it back.
      if (respectDeleted && mashovDeleted[b.childGuid] && !list.find((t) => t.mashov && t.mashov.childGuid === b.childGuid)) {
        continue;
      }
      try {
        const { rows, cells } = parsedToRowsCells(b);
        const ev = Array.isArray(b.events) ? b.events : [];
        const bh = Array.isArray(b.behavior) ? b.behavior : [];
        const mashov = {
          semel: Number(cfg.semel), schoolName: b.schoolName || cfg.schoolName || "", year: Number(cfg.year),
          username: String(cfg.username), password: String(cfg.password),
          childGuid: b.childGuid, childName: b.name || "",
        };
        // upsert: update an existing board bound to this child, else create a new one
        const existing = list.find((t) => t.mashov && t.mashov.childGuid === b.childGuid);
        const id = existing ? existing.id : uid7();
        if (!firstId) firstId = id;
        if (existing) {
          await update(ref(db, `${base(uid)}/timetables/${id}`), { rows, cells, mashov, events: ev, behavior: bh });
        } else {
          await update(ref(db, `${base(uid)}/timetables`), {
            [id]: { name: (b.name || "משו״ב").trim(), order: Date.now() + i, rows, cells, mashov, events: ev, behavior: bh },
          });
        }
        lessons += (b.entries || []).length;
        events += ev.length;
        ok += 1;
      } catch {
        failed.push(b.name || "ילד/ה");
      }
    }
    if (firstId) await update(ref(db, base(uid)), { activeTimetableId: firstId });
    return { created: ok, lessons, events, failed };
  };
  // used by the config form (create a new board OR replace an existing one), stores the config for one-click pulls
  const saveMashovBoard = async (cfg, ctx) => {
    const editId = ctx && ctx.mode === "edit" && ctx.id ? ctx.id : null;
    // editing a board already bound to a specific child → pull just that child
    const boundChild = editId && timetables[editId] && timetables[editId].mashov && timetables[editId].mashov.childGuid;
    const out = await fetchMashov({ ...cfg, childGuid: cfg.childGuid || boundChild || undefined });
    // parent → the worker returns all children's boards in one response.
    // This is an explicit re-setup, so forget past deletions and bring all children back.
    if (out.multi) {
      await update(ref(db, base(uid)), { mashovDeleted: null });
      const res = await saveBoardsFromMulti(cfg, out, { respectDeleted: false });
      return { ...res, multi: true };
    }
    const { rows, cells } = parsedToRowsCells(out);
    const events = Array.isArray(out.events) ? out.events : [];
    const behavior = Array.isArray(out.behavior) ? out.behavior : [];
    const mashov = {
      semel: Number(cfg.semel), schoolName: cfg.schoolName || "", year: Number(cfg.year),
      username: String(cfg.username), password: String(cfg.password),
      childGuid: cfg.childGuid || boundChild || "", childName: out.name || "",
    };
    // explicit setup for this child → clear any past "deleted" mark for it
    if (mashov.childGuid) await update(ref(db, `${base(uid)}/mashovDeleted`), { [mashov.childGuid]: null });
    if (editId) {
      await update(ref(db, `${base(uid)}/timetables/${editId}`), { rows, cells, mashov, events, behavior });
      await update(ref(db, base(uid)), { activeTimetableId: editId });
    } else {
      const id = uid7();
      await update(ref(db, `${base(uid)}/timetables`), {
        [id]: { name: (out.name || "").trim() || "משו״ב", order: Date.now(), rows, cells, mashov, events, behavior },
      });
      await update(ref(db, base(uid)), { activeTimetableId: id });
    }
    return { lessons: (out.entries || []).length, events: events.length, diag: out._diag, created: 1 };
  };
  // shared feedback for a Mashov result (toast + raw sample when nothing mapped)
  const showMashovResult = (res) => {
    if (!res) return;
    if (res.multi) {
      const failedTxt = res.failed && res.failed.length ? ` · לא נמשך: ${res.failed.join(", ")} (בית ספר אחר)` : "";
      setMashovToast(
        res.created > 0
          ? { ok: true, msg: `סונכרנו ${res.created} לוחות · ${res.lessons} שיעורים ✓${failedTxt}` }
          : { ok: false, msg: "לא הצלחתי למשוך מערכת לאף אחד מהילדים" }
      );
    } else if (res.lessons === 0) {
      setMashovToast({ ok: false, msg: "התחברות הצליחה, אך לא זוהו שיעורים במערכת" });
      if (res.diag && res.diag.sample) {
        window.alert(
          "משו״ב התחבר אך המיפוי ריק. שלח לי את הדגימה הזו כדי שאתקן:\n\n" +
            JSON.stringify(res.diag.sample, null, 2).slice(0, 1500)
        );
      }
    } else {
      setMashovToast({ ok: true, msg: `נמשכו ${res.lessons} שיעורים · ${res.events} מבחנים/מטלות ✓` });
    }
    setTimeout(() => setMashovToast(null), 6000);
  };
  // one-click pull for the active board (gear-side button)
  const pullMashovActive = async () => {
    if (!active || !active.mashov) return;
    setMashovBusy(true);
    setMashovToast(null);
    try {
      // drop childGuid so a parent account re-syncs ALL children (one board each)
      const cfg = { ...active.mashov };
      delete cfg.childGuid;
      const out = await fetchMashov(cfg);
      if (out.multi) {
        const res = await saveBoardsFromMulti(cfg, out);
        showMashovResult({ ...res, multi: true });
        return;
      }
      const { rows, cells } = parsedToRowsCells(out);
      const events = Array.isArray(out.events) ? out.events : [];
      const behavior = Array.isArray(out.behavior) ? out.behavior : [];
      await update(ref(db, `${base(uid)}/timetables/${active.id}`), { rows, cells, events, behavior });
      showMashovResult({ lessons: (out.entries || []).length, events: events.length, diag: out._diag });
    } catch (e) {
      setMashovToast({ ok: false, msg: "משיכה נכשלה — " + (e && e.message ? e.message : e) });
      setTimeout(() => setMashovToast(null), 6000);
    } finally {
      setMashovBusy(false);
    }
  };

  /* ---------- auth ---------- */
  const signIn = async () => {
    setAuthBusy(true);
    try {
      // installed PWA / iOS home-screen → popup doesn't work, go straight to redirect
      if (isStandalone()) {
        await signInWithRedirect(auth, googleProvider);
        return; // page navigates away to Google
      }
      await signInWithPopup(auth, googleProvider);
    } catch (e) {
      // popup blocked / unsupported (some in-app browsers) → fall back to redirect
      try {
        await signInWithRedirect(auth, googleProvider);
        return;
      } catch {
        alert("ההתחברות נכשלה. נסו שוב.");
      }
    } finally {
      setAuthBusy(false);
    }
  };
  const signOutNow = () => signOut(auth).catch(() => {});
  const doRefresh = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.update().catch(() => {})));
      }
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {}
    window.location.reload();
  };

  /* ---------- reminders (Web Push) ---------- */
  const reminders = (data && data.reminders) || { enabled: false, morning: true, morningHm: "07:00", events: true, eventHm: "18:00" };
  const saveReminders = (s) => set(ref(db, `${base(uid)}/reminders`), s);
  // returns {ok} — registers SW, (un)subscribes, syncs plan to the push worker.
  // `silent` (background auto-sync): never prompt or alert — only proceed if the
  // user already granted notification permission, otherwise quietly do nothing.
  const syncReminders = async (s, silent = false) => {
    if (!pushSupported()) {
      if (!silent) alert("הדפדפן/המכשיר לא תומך בהתראות. ב-iPhone צריך קודם להתקין למסך הבית.");
      return { ok: false };
    }
    const reg = await navigator.serviceWorker.register("/sw-push.js");
    await navigator.serviceWorker.ready;
    try {
      if (s.enabled) {
        // in the background just check; only a real user action may prompt
        const perm = silent ? Notification.permission : await Notification.requestPermission();
        if (perm !== "granted") {
          if (!silent) alert("כדי לקבל תזכורות צריך לאשר התראות.");
          return { ok: false };
        }
        let subm = await reg.pushManager.getSubscription();
        if (!subm)
          subm = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlB64ToUint8(VAPID_PUBLIC),
          });
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Jerusalem";
        const plan = [
          ...(s.morning !== false ? buildReminderPlan(active, rows, "each") : []),
          ...(s.events !== false ? buildEventPlan(list, s.eventHm || "18:00") : []),
        ];
        const res = await fetch(`${PUSH_ENDPOINT}/sync`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subscription: subm.toJSON(), tz, plan, enabled: true,
            morning: s.morning !== false, morningHm: s.morningHm || "07:00",
          }),
        });
        return { ok: res.ok, subscription: subm.toJSON() };
      } else {
        const subm = await reg.pushManager.getSubscription();
        if (subm)
          await fetch(`${PUSH_ENDPOINT}/sync`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ subscription: subm.toJSON(), enabled: false }),
          });
        return { ok: true };
      }
    } catch (e) {
      if (!silent) alert("שגיאה בהגדרת התזכורות: " + (e && e.message ? e.message : e));
      return { ok: false };
    }
  };

  // keep the push worker's plan fresh when the active timetable / settings change
  useEffect(() => {
    if (!uid || !reminders.enabled || !active) return;
    const t = setTimeout(() => { syncReminders(reminders, true); }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, reminders.enabled, reminders.morning, reminders.morningHm, reminders.events, reminders.eventHm, activeId, JSON.stringify(active && active.cells), JSON.stringify(rows), JSON.stringify(list.map((t) => t.events || []))]);

  const shareWhatsApp = () => {
    const link = "https://mie.co.il/schedule";
    const text = `מערכת שעות — אפליקציה נוחה לניהול מערכת השעות השבועית שלך 🗓️\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };
  const doInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice.catch(() => {});
      setInstallPrompt(null);
      setShowInstall(false);
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  /* ---------- navigation ---------- */
  const go = (dir) => {
    if (mode === "day") setCursor((c) => addDays(c, dir));
    else if (mode === "week") setCursor((c) => addDays(c, 7 * dir));
    else setCursor((c) => new Date(c.getFullYear(), c.getMonth() + dir, Math.min(c.getDate(), 28)));
  };
  const openDay = (d) => {
    setCursor(atMidnight(d));
    setManualMode("day");
  };

  const rangeLabel = () => {
    if (mode === "day") return `יום ${HE_DAYS[cursor.getDay()]} · ${fmtDMY(cursor)}`;
    if (mode === "week") {
      const s = startOfWeek(cursor);
      return `${fmtDM(s)}–${fmtDM(addDays(s, 5))}`;
    }
    return `${HE_MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  };

  /* ---------- render ---------- */
  // the splash animation IS the loading indicator — keep it mounted across the
  // auth-undefined → login/app transitions (same position under Shell) so it never
  // restarts, and never show a separate "loading" text before it.
  const splashEl = showSplash ? (
    <Splash onReveal={() => setContentReady(true)} onDone={() => setShowSplash(false)} />
  ) : null;
  if (authUser === undefined) {
    return (
      <Shell dir="rtl">
        <Global />
        {splashEl}
        {!showSplash && <Loading>טוען…</Loading>}
      </Shell>
    );
  }
  if (!authUser) {
    return (
      <Shell dir="rtl">
        <Global />
        {splashEl}
        <Gate>
          <GateTag>שבועי · פרטי · למסך הבית</GateTag>
          <GateTitle>
            מערכת
            <br />
            שעות
          </GateTitle>
          <GateSub>
            הלוח השבועי שלך — מוגן, פרטי, עם תזכורות.
            <br />
            התחבר כדי להתחיל.
          </GateSub>
          <GBtn onClick={signIn} disabled={authBusy}>
            <GG>G</GG>
            {authBusy ? "מתחבר…" : "כניסה עם Google"}
          </GBtn>
          <GateFoot>כפיר מילר ©</GateFoot>
        </Gate>
      </Shell>
    );
  }

  const cellFor = (rowId, dayKey) =>
    (active && active.cells && active.cells[rowId] && active.cells[rowId][dayKey]) || null;
  const wx = weather.status === "ok" ? weather : null;
  const curEmoji = wx && wx.current ? wmo(wx.current.code)[0] : "⛅";
  // only warn when rain is actually likely tomorrow — real amount or a high chance,
  // not a trace of drizzle that Open-Meteo's daily weather_code over-flags
  const tmrw = wx && wx.daily && wx.daily[1];
  const tomorrowRain = !!(tmrw && isRainy(tmrw.code) && (tmrw.precip >= 1 || tmrw.pop >= 50));

  return (
    <Shell dir="rtl">
      <Global />
      {splashEl}

      {isOwner && (
        <SectionBar>
          <SectionBtn $on={section === "kids"} onClick={() => { setSection("kids"); setViewMenuOpen(false); }}>הילדים</SectionBtn>
          <SectionBtn $on={section === "me"} onClick={() => { setSection("me"); setViewMenuOpen(false); }}>אני</SectionBtn>
        </SectionBar>
      )}

      {/* masthead */}
      <Masthead>
        <MastMeta>
          <Tag>
            {HE_DAYS[today.getDay()]} · {fmtDMY(today)} · {hebrewDate(today)}
          </Tag>
          <WeatherChip onClick={() => setWeatherOpen(true)} title="מזג האוויר">
            <span>{curEmoji}</span>
            {wx && wx.current ? <b>{wx.current.temp}°</b> : <b>·</b>}
          </WeatherChip>
          <WeatherChip onClick={() => setNewsOpen(true)} title="חדשות" aria-label="חדשות">
            <span>📰</span>
          </WeatherChip>
        </MastMeta>
        <MastMain>
          <BrandWrap>
            <Brand onClick={() => setViewMenuOpen((o) => !o)} title="החלפת תצוגה">
              {section === "me"
                ? (meView === "meetings" ? "פגישות" : "אירועי משפחה")
                : view === "schedule" ? "מערכת שעות" : view === "homework" ? "שיעורי בית" : view === "mail" ? "דואר נכנס" : "התנהגות"}
              <Tri aria-hidden="true">▾</Tri>
            </Brand>
            {viewMenuOpen && (
              <>
                <MenuBackdrop onClick={() => setViewMenuOpen(false)} />
                <ViewMenu>
                  {section === "me" ? (
                    <>
                      <ViewItem $on={meView === "events"} onClick={() => { setMeView("events"); setViewMenuOpen(false); }}>אירועי משפחה</ViewItem>
                      <ViewItem $on={meView === "meetings"} onClick={() => { setMeView("meetings"); setViewMenuOpen(false); }}>פגישות</ViewItem>
                    </>
                  ) : (
                    <>
                      <ViewItem $on={view === "schedule"} onClick={() => { setView("schedule"); setViewMenuOpen(false); }}>מערכת שעות</ViewItem>
                      <ViewItem $on={view === "homework"} onClick={() => { setView("homework"); setViewMenuOpen(false); }}>שיעורי בית ומבחנים</ViewItem>
                      <ViewItem $on={view === "behavior"} onClick={() => { setView("behavior"); setViewMenuOpen(false); }}>התנהגות בשיעור</ViewItem>
                      <ViewItem $on={view === "mail"} onClick={() => { setView("mail"); setViewMenuOpen(false); }}>דואר נכנס{mail && mail.unread ? ` (${mail.unread})` : ""}</ViewItem>
                    </>
                  )}
                </ViewMenu>
              </>
            )}
          </BrandWrap>
          <MastControls>
            {section === "me" ? (
              <MashovBtn onClick={refreshMeetings} disabled={gcalStatus === "connecting"} title="רענון יומן" aria-label="רענון יומן">
                {gcalStatus === "connecting" ? (
                  <MiniSpin />
                ) : (
                  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12a9 9 0 1 1-3-6.7" />
                    <polyline points="21 3 21 9 15 9" />
                  </svg>
                )}
              </MashovBtn>
            ) : (
              <>
                {active && active.mashov && (
                  <MashovBtn onClick={pullMashovActive} disabled={mashovBusy} title="משוך ממשו״ב" aria-label="משוך ממשו״ב">
                    {mashovBusy ? (
                      <MiniSpin />
                    ) : (
                      <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12a9 9 0 1 1-3-6.7" />
                        <polyline points="21 3 21 9 15 9" />
                      </svg>
                    )}
                  </MashovBtn>
                )}
                <BellBtn $on={reminders.enabled} onClick={() => setRemindersOpen(true)} title="תזכורות" aria-label="תזכורות">
                  <IconBell width={18} height={18} />
                </BellBtn>
              </>
            )}
            {customAvatar || (authUser.photoURL && !avatarErr) ? (
              <Ava
                src={customAvatar || authUser.photoURL}
                alt=""
                referrerPolicy="no-referrer"
                onError={() => setAvatarErr(true)}
                onClick={onPickAvatar}
                style={{ cursor: "pointer" }}
                title="החלפת תמונת פרופיל"
              />
            ) : (
              <AvaX onClick={onPickAvatar} style={{ cursor: "pointer" }} title="הוספת תמונת פרופיל">👤</AvaX>
            )}
            {mashovToast && <MashovToast $ok={mashovToast.ok}>{mashovToast.msg}</MashovToast>}
            <GearWrap>
              <GearBtn onClick={() => setMenuOpen((o) => !o)} title="הגדרות" aria-label="הגדרות">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </GearBtn>
              {menuOpen && (
                <>
                  <MenuBackdrop onClick={() => setMenuOpen(false)} />
                  <GearMenu>
                    <GearItem onClick={() => { setMenuOpen(false); setNamePrompt({ mode: "create", value: "" }); }}>
                      <span>לוח חדש</span><IconPlus />
                    </GearItem>
                    <GearItem onClick={() => { setMenuOpen(false); onPickImage(); }}>
                      <span>מתוך תמונה</span><CamIcon src="/cam-icon.png" alt="" style={{ marginInlineEnd: 0, height: 20 }} />
                    </GearItem>
                    <GearItem onClick={() => { setMenuOpen(false); setMashovConfig({ mode: "create" }); }}>
                      <span>יבוא ממשו״ב</span><IconDownload />
                    </GearItem>
                    {section === "me" && meView === "meetings" && (gcalStatus === "connected" || gcalReadToken() || gcalReadRefresh()) && (
                      <GearItem onClick={() => { setMenuOpen(false); disconnectCalendar(); }}>
                        <span>התנתקות מפגישות</span><IconLogout />
                      </GearItem>
                    )}
                    <GearDivider />
                    <GearItem onClick={() => { setMenuOpen(false); setAboutOpen(true); }}>
                      <span>אודות</span><IconInfo />
                    </GearItem>
                    <GearItem onClick={() => { setMenuOpen(false); shareWhatsApp(); }}>
                      <span>שלחי לחברה</span><IconSend />
                    </GearItem>
                    <GearItem onClick={() => { setMenuOpen(false); signOutNow(); }}>
                      <span>התנתקות</span><IconLogout />
                    </GearItem>
                    <GearFooter>
                      <GearVersion>גרסה {APP_VERSION}</GearVersion>
                      <RefreshBtn onClick={() => { setMenuOpen(false); doRefresh(); }} title="רענון" aria-label="רענון">
                        <IconRefresh width={17} height={17} />
                      </RefreshBtn>
                    </GearFooter>
                  </GearMenu>
                </>
              )}
            </GearWrap>
          </MastControls>
        </MastMain>
      </Masthead>

      {isOwner && section === "me" ? (
        <MeSection meView={meView} meetings={gcalMeetings} gcalStatus={gcalStatus} onConnect={connectCalendar} />
      ) : (
      <>
      {/* timetable selector */}
      {list.length > 0 && (
        <Collections>
          <CollScroll>
            {list.map((t) => (
              <Chip
                key={t.id}
                $on={t.id === activeId}
                onClick={() => (t.id === activeId ? setNamePrompt({ mode: "rename", id: t.id, value: t.name }) : setActive(t.id))}
                title={t.id === activeId ? "לחצו לעריכת הלוח" : "מעבר ללוח"}
              >
                {t.name}
                {t.id === activeId && <ChipEdit aria-hidden="true">✎</ChipEdit>}
              </Chip>
            ))}
          </CollScroll>
          {view === "schedule" && (
            <Segmented>
              <Seg $on={mode === "day" && sameDay(cursor, today)} onClick={() => { setManualMode("day"); setCursor(today); }}>היום</Seg>
              <Seg $on={mode === "day" && sameDay(cursor, addDays(today, 1))} onClick={() => { setManualMode("day"); setCursor(addDays(today, 1)); }}>מחר</Seg>
              <Seg $on={mode === "week"} onClick={() => setManualMode("week")}>שבוע</Seg>
              <Seg $on={mode === "month"} onClick={() => setManualMode("month")}>חודש</Seg>
            </Segmented>
          )}
        </Collections>
      )}

      {/* nav bar: only in month, or in day when not today/tomorrow */}
      {view === "schedule" &&
        ((mode === "day" && !sameDay(cursor, today) && !sameDay(cursor, addDays(today, 1))) || mode === "month") && (
        <NavBar>
          <NavGroup>
            <NavArrow onClick={() => go(-1)} title="הקודם">‹</NavArrow>
            <RangeLabel dir={mode === "day" ? "rtl" : "ltr"} onClick={() => setCursor(today)} title="חזרה להיום">
              {rangeLabel()}
            </RangeLabel>
            <NavArrow onClick={() => go(1)} title="הבא">›</NavArrow>
          </NavGroup>
        </NavBar>
      )}

      {(showInstall || isIOS()) && !isStandalone() && (
        <InstallBar onClick={doInstall}>
          <span>◱ התקן למסך הבית</span>
          <em>{isIOS() ? "שיתוף ← הוסף למסך הבית" : "לחץ להתקנה"}</em>
        </InstallBar>
      )}

      {/* views */}
      <ViewFade $show={contentReady}>
      {dbError ? (
        <DbError>
          <DbErrTitle>{dbError === "permission" ? "אין הרשאה לטעון את הנתונים" : "שגיאה בטעינת הנתונים"}</DbErrTitle>
          <DbErrText>
            {dbError === "permission"
              ? "כללי מסד הנתונים עדיין לא נפרסו. הריצו בטרמינל:"
              : "נסו לרענן את הדף."}
          </DbErrText>
          {dbError === "permission" && <DbErrCode>firebase deploy --only database</DbErrCode>}
        </DbError>
      ) : !loaded ? (
        <Loading>טוען…</Loading>
      ) : list.length === 0 ? (
        <EmptyState>
          <EmptyTitle>אין עדיין לוחות</EmptyTitle>
          <EmptyText>איך תרצו להתחיל?</EmptyText>
          <EmptyBtns>
            <BigBtn onClick={() => setNamePrompt({ mode: "create", value: "" })}>הוספת לוח</BigBtn>
            <GhostBtn onClick={onPickImage}><CamIcon src="/cam-icon.png" alt="" />מתוך תמונה</GhostBtn>
            <GhostBtn onClick={() => setMashovConfig({ mode: "create" })}>יבוא ממשו״ב</GhostBtn>
          </EmptyBtns>
        </EmptyState>
      ) : !active ? (
        <Loading>טוען…</Loading>
      ) : view === "homework" ? (
        <ListView items={active.events} kind="homework" who={(active.name || "").trim().split(/\s+/)[0]} />
      ) : view === "behavior" ? (
        <ListView items={active.behavior} kind="behavior" who={(active.name || "").trim().split(/\s+/)[0]} />
      ) : view === "mail" ? (
        <MailView mail={mail} />
      ) : mode === "day" ? (
        <DayView
          date={cursor}
          rows={rows}
          cellFor={cellFor}
          holidays={holidaysFor(cursor)}
          events={eventsFor(cursor)}
          isToday={sameDay(cursor, today)}
          isTomorrow={sameDay(cursor, addDays(today, 1))}
          tomorrowRain={tomorrowRain}
          isMashov={!!(active && active.mashov)}
          onEdit={(rowId, dayKey) => setCellEdit({ rowId, dayKey })}
          onEditRows={() => setRowsOpen(true)}
        />
      ) : mode === "week" ? (
        <WeekGrid
          weekStart={startOfWeek(cursor)}
          rows={rows}
          cellFor={cellFor}
          holidaysFor={holidaysFor}
          eventsFor={eventsFor}
          today={today}
          onEdit={(rowId, dayKey) => setCellEdit({ rowId, dayKey })}
          onEditRows={() => setRowsOpen(true)}
          onPickDay={openDay}
        />
      ) : (
        <MonthView
          cursor={cursor}
          rows={rows}
          cellFor={cellFor}
          holidaysFor={holidaysFor}
          eventsFor={eventsFor}
          today={today}
          onPick={openDay}
        />
      )}
      </ViewFade>
      </>
      )}


      {cellEdit && (
        <CellEditor
          value={cellFor(cellEdit.rowId, cellEdit.dayKey)}
          rowLabel={(() => {
            const r = rows.find((x) => x.id === cellEdit.rowId);
            return r ? `${r.title || ""} · ${r.time || ""}` : "";
          })()}
          dayLabel={(DAYS.find((d) => d.key === cellEdit.dayKey) || {}).name}
          onClose={() => setCellEdit(null)}
          onSave={(payload) => {
            setCell(cellEdit.rowId, cellEdit.dayKey, payload);
            setCellEdit(null);
          }}
          onClear={() => {
            setCell(cellEdit.rowId, cellEdit.dayKey, null);
            setCellEdit(null);
          }}
        />
      )}

      {rowsOpen && <RowsEditor rows={rows} onClose={() => setRowsOpen(false)} onSave={saveRows} />}

      {namePrompt && (
        <NamePromptModal
          mode={namePrompt.mode}
          initial={namePrompt.value}
          connectedMashov={!!(namePrompt.id && timetables[namePrompt.id] && timetables[namePrompt.id].mashov)}
          onClose={() => setNamePrompt(null)}
          onSubmit={(name) => {
            if (namePrompt.mode === "create") createTimetable(name);
            else renameTimetable(namePrompt.id, name);
            setNamePrompt(null);
          }}
          onFromImage={namePrompt.mode === "create" ? () => { setNamePrompt(null); onPickImage(); } : undefined}
          onReplaceFromImage={namePrompt.mode === "rename" ? () => { setNamePrompt(null); onPickImage(namePrompt.id); } : undefined}
          onFromMashov={() =>
            namePrompt.mode === "create"
              ? (setNamePrompt(null), setMashovConfig({ mode: "create" }))
              : (setNamePrompt(null), setMashovConfig({ mode: "edit", id: namePrompt.id }))
          }
          onDelete={namePrompt.mode === "rename" ? () => { setNamePrompt(null); deleteTimetable(namePrompt.id); } : undefined}
        />
      )}

      {mashovConfig && (
        <MashovSheet
          initial={mashovConfig.id && timetables[mashovConfig.id] ? timetables[mashovConfig.id].mashov : null}
          onClose={() => setMashovConfig(null)}
          onSubmit={async (cfg) => {
            const res = await saveMashovBoard(cfg, mashovConfig);
            setMashovConfig(null);
            showMashovResult(res);
          }}
        />
      )}

      {weatherOpen && (
        <WeatherSheet weather={weather} holidaysFor={holidaysFor} onClose={() => setWeatherOpen(false)} />
      )}

      {newsOpen && <NewsSheet news={news} onClose={() => setNewsOpen(false)} />}

      {remindersOpen && (
        <RemindersSheet
          settings={reminders}
          supported={pushSupported()}
          onClose={() => setRemindersOpen(false)}
          onSave={async (s) => {
            const r = await syncReminders(s);
            if (r.ok) saveReminders(s);
            setRemindersOpen(false);
          }}
          onTest={async () => {
            if (!pushSupported()) return alert("לא נתמך במכשיר הזה.");
            const reg = await navigator.serviceWorker.register("/sw-push.js");
            await navigator.serviceWorker.ready;
            const perm = await Notification.requestPermission();
            if (perm !== "granted") return alert("צריך לאשר התראות קודם.");
            let subm = await reg.pushManager.getSubscription();
            if (!subm) subm = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8(VAPID_PUBLIC) });
            const res = await fetch(`${PUSH_ENDPOINT}/test`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ subscription: subm.toJSON() }),
            });
            alert(res.ok ? "נשלחה התראת בדיקה 🔔" : "שליחת הבדיקה נכשלה.");
          }}
        />
      )}

      {aboutOpen && (
        <Sheet onClose={() => setAboutOpen(false)} title="אודות">
          <AboutText>
            <b>מערכת שעות</b> — אפליקציה שבועית לניהול מערכת השעות שלך.
            <br />
            כל הזכויות שמורות © לכפיר מילר.
          </AboutText>
          <AboutMail href="mailto:miller.kfir@gmail.com">miller.kfir@gmail.com</AboutMail>
        </Sheet>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = "";
          const target = importTargetRef.current;
          importTargetRef.current = null;
          if (f) importFromImage(f, target);
        }}
      />
      <input
        ref={avatarFileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = "";
          if (f) saveAvatar(f);
        }}
      />

      {importing && (
        <ImportOverlay>
          <ImportBox>
            <ImportSpin />
            <ImportText>קורא את מערכת השעות מהתמונה…</ImportText>
            <ImportSub>עוד רגע — בונה את הלוח</ImportSub>
          </ImportBox>
        </ImportOverlay>
      )}

      {iosHint && (
        <Sheet onClose={() => setIosHint(false)} title="התקנה למסך הבית">
          <IosSteps>
            <li>
              פתח את התפריט <b>שיתוף</b> בדפדפן <IosGlyph>􀈂</IosGlyph> (הריבוע עם החץ למעלה).
            </li>
            <li>
              גלול ובחר <b>״הוספה למסך הבית״</b>.
            </li>
            <li>אשר, והאפליקציה תופיע כמו אפליקציה רגילה.</li>
          </IosSteps>
        </Sheet>
      )}
    </Shell>
  );
};

function TimeStack({ time }) {
  const [a, b] = splitTime(time);
  return (
    <RowTime>
      <span>{a}</span>
      {b && <span>{b}</span>}
    </RowTime>
  );
}

/* ============================================================
   List view — homework/exams or behavior
   ============================================================ */
function meEventTitle(e) {
  const n = e.firstName || e.name;
  if (e.type === "birthday") return e.age ? `🎂 יום הולדת ${e.age} ל${n}` : `🎂 יום הולדת ל${n}`;
  if (e.type === "anniversary") return e.age ? `💍 ${e.age} שנות נישואין — ${n}` : `💍 יום נישואין — ${n}`;
  return `🕯️ יום הזכרון ל${n}`;
}
function colorForTitle(t) {
  const s = String(t || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return SWATCHES[h % SWATCHES.length];
}
const LUNCH_RE = /(lunch|ארוחת צהריים|צהריים|ארוחה)/i;
// Google-Calendar-style working-location icons (blue, filled)
function WlIcon({ type, size = 18 }) {
  const p = { viewBox: "0 0 24 24", width: size, height: size, fill: "#6ba6e8", style: { verticalAlign: "-3px", marginInlineStart: 4, flex: "0 0 auto" } };
  if (type === "homeOffice") return (<svg {...p}><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" /></svg>);
  if (type === "officeLocation") return (<svg {...p}><path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" /></svg>);
  if (type === "customLocation") return (<svg {...p}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" /></svg>);
  return null;
}
const hhmm = (iso) => { if (!iso || iso.length <= 10) return ""; const d = new Date(iso); return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0"); };

function MeetingRow({ m, index }) {
  const allDay = m.when && m.when.length <= 10;
  const t = allDay ? "" : `${hhmm(m.when)}-${hhmm(m.end)}`;
  const isLunch = LUNCH_RE.test(m.title || "");
  const color = isLunch ? "#E8C7A8" : colorForTitle(m.title || "");
  const openVideo = (e) => { e.stopPropagation(); window.open(m.video, "_blank", "noopener"); };
  return (
    <DayRow style={{ animationDelay: `${index * 45}ms` }}>
      <DayRowTime as="div">
        {allDay ? <RowNum>כל היום</RowNum> : <TimeStack time={t} />}
      </DayRowTime>
      <DayRowBody $bg={color} style={{ color: textOn(color), cursor: "default" }}>
        <DayRowText>
          {m.title}
          {m.location && <DayRowTeacher>{m.location}</DayRowTeacher>}
        </DayRowText>
        {m.video && <VideoBtn onClick={openVideo} title="הצטרפות לשיחת וידאו">📹 וידאו</VideoBtn>}
        {isLunch && <LunchImg src="/lunch.png" alt="ארוחת צהריים" />}
      </DayRowBody>
    </DayRow>
  );
}

// one day×slot cell in the meetings week grid — the colored block FILLS the cell (like the kids)
function MeetCell({ items }) {
  if (!items || items.length === 0) return <MWkCell><Plus>·</Plus></MWkCell>;
  return (
    <MWkCell>
      {items.map((m, i) => {
        const isLunch = LUNCH_RE.test(m.title || "");
        const color = isLunch ? "#E8C7A8" : colorForTitle(m.title || "");
        return (
          <MWkItem
            key={i}
            style={{ background: color, color: textOn(color), cursor: m.video ? "pointer" : "default" }}
            onClick={m.video ? () => window.open(m.video, "_blank", "noopener") : undefined}
            title={m.title}
          >
            {m.title}{m.video ? " 📹" : ""}
          </MWkItem>
        );
      })}
    </MWkCell>
  );
}

// week view for meetings — a real grid like the kids' timetable: a shared time
// column (start + end) + a column per day, rows aligned to each meeting's time span.
function MeetingsWeekGrid({ byDay, workByDay, onPickDay }) {
  const today = atMidnight(new Date());
  const start = startOfWeek(today);
  const days = Array.from({ length: 6 }, (_, i) => addDays(start, i)); // ראשון–שישי, like the kids
  const week = days.flatMap((d) => byDay[ymd(d)] || []);
  const isAllDay = (m) => m.when && m.when.length <= 10;
  const slotKey = (m) => `${hhmm(m.when)}-${hhmm(m.end)}`; // "08:00-09:00"
  const anyAllDay = week.some(isAllDay);
  const slots = [...new Set(week.filter((m) => !isAllDay(m)).map(slotKey))].sort();
  const at = (d, slot) => (byDay[ymd(d)] || []).filter((m) => !isAllDay(m) && slotKey(m) === slot);
  const allDayAt = (d) => (byDay[ymd(d)] || []).filter(isAllDay);
  return (
    <GridWrap>
      <Grid>
        <Corner>שעה</Corner>
        {days.map((d) => {
          const wl = workByDay[ymd(d)];
          return (
            <HeadCell key={ymd(d)} $today={sameDay(d, today)} onClick={() => onPickDay && onPickDay(d)} title={`מעבר ליום ${HE_DAYS[d.getDay()]}`}>
              <Hd>{HE_DAYS[d.getDay()]}{wl && <WlIcon type={wl} size={14} />}</Hd>
              <HdDate>{fmtDM(d)}</HdDate>
            </HeadCell>
          );
        })}
        {anyAllDay && (
          <React.Fragment>
            <RowLabel><MWkSlot>כל היום</MWkSlot></RowLabel>
            {days.map((d) => <MeetCell key={ymd(d)} items={allDayAt(d)} />)}
          </React.Fragment>
        )}
        {slots.map((slot) => (
          <React.Fragment key={slot}>
            <RowLabel><TimeStack time={slot} /></RowLabel>
            {days.map((d) => <MeetCell key={ymd(d)} items={at(d, slot)} />)}
          </React.Fragment>
        ))}
        {slots.length === 0 && !anyAllDay && (
          <React.Fragment>
            <RowLabel><MWkSlot>—</MWkSlot></RowLabel>
            {days.map((d) => <MeetCell key={ymd(d)} items={[]} />)}
          </React.Fragment>
        )}
      </Grid>
    </GridWrap>
  );
}

// month view for meetings — a calendar grid like the kids', with a count per day
function MeetingsMonthGrid({ byDay, monthCursor, today, onPick, onNav }) {
  const first = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const month = monthCursor.getMonth();
  return (
    <MeWrap>
      <NavBar style={{ marginTop: 0 }}>
        <NavGroup>
          <NavArrow onClick={() => onNav(-1)} title="חודש קודם">‹</NavArrow>
          <RangeLabel dir="ltr">{HE_MONTHS[month]} {monthCursor.getFullYear()}</RangeLabel>
          <NavArrow onClick={() => onNav(1)} title="חודש הבא">›</NavArrow>
        </NavGroup>
      </NavBar>
      <MonthHead>
        {HE_DAYS_SHORT.map((s, i) => <MHd key={i} $weekend={i === 6}>{s}</MHd>)}
      </MonthHead>
      <MonthGrid>
        {cells.map((date, i) => {
          const inMonth = date.getMonth() === month;
          const items = byDay[ymd(date)] || [];
          const isToday = sameDay(date, today);
          return (
            <MCell key={i} $in={inMonth} $today={isToday} $weekend={date.getDay() === 6} onClick={() => onPick(date)}>
              <MNum $today={isToday}>{date.getDate()}</MNum>
              {items.length > 0 && <MEv title={items.map((m) => m.title).join(" · ")}>{items.length}</MEv>}
            </MCell>
          );
        })}
      </MonthGrid>
    </MeWrap>
  );
}

function MeetingsView({ meetings, range, dayCursor, monthCursor, onPickDay, onMonthNav }) {
  const list = Array.isArray(meetings) ? meetings : [];
  const real = list.filter((m) => (m.kind || "default") !== "workingLocation");
  const workByDay = {};
  list.forEach((m) => { if (m.kind === "workingLocation" && m.when) workByDay[ymd(new Date(m.when))] = m.workLoc; });

  const today = atMidnight(new Date());
  const tomorrow = addDays(today, 1);
  const byDay = {};
  real.forEach((m) => { if (!m.when) return; const d = new Date(m.when); if (isNaN(d)) return; (byDay[ymd(d)] = byDay[ymd(d)] || []).push(m); });
  const dayMeetings = (d) => (byDay[ymd(d)] || []).slice().sort((a, b) => String(a.when).localeCompare(String(b.when)));

  if (range === "week") return <MeetingsWeekGrid byDay={byDay} workByDay={workByDay} onPickDay={onPickDay} />;
  if (range === "month") return <MeetingsMonthGrid byDay={byDay} monthCursor={monthCursor || today} today={today} onPick={onPickDay} onNav={onMonthNav} />;

  // single day: today / tomorrow / a picked day
  const d = range === "tomorrow" ? tomorrow : range === "day" ? (dayCursor || today) : today;
  const items = dayMeetings(d);
  const wl = workByDay[ymd(d)];
  return (
    <MeWrap>
      {range === "day" && (
        <NavBar style={{ marginTop: 0 }}>
          <NavGroup>
            <NavArrow onClick={() => onPickDay(addDays(d, -1))} title="יום קודם">‹</NavArrow>
            <RangeLabel dir="ltr" onClick={() => onPickDay(today)} title="חזרה להיום">{fmtDMY(d)}</RangeLabel>
            <NavArrow onClick={() => onPickDay(addDays(d, 1))} title="יום הבא">›</NavArrow>
          </NavGroup>
        </NavBar>
      )}
      <DayHead $today={sameDay(d, today)}>
        <DayName>
          {HE_DAYS[d.getDay()]}
          {sameDay(d, today) && <TodayDot>היום</TodayDot>}
          {sameDay(d, tomorrow) && <TodayDot>מחר</TodayDot>}
          {wl && <WorkIcon as="span" title={wl === "homeOffice" ? "בית" : wl === "officeLocation" ? "משרד" : "מיקום"}><WlIcon type={wl} size={20} /></WorkIcon>}
        </DayName>
        <DayDate>{fmtDMY(d)}</DayDate>
      </DayHead>
      {items.length === 0 ? (
        <MeEmpty style={{ margin: "0 0 8px" }}>אין פגישות</MeEmpty>
      ) : (
        <DayList>
          {items.map((m, i) => <MeetingRow key={i} m={m} index={i} />)}
        </DayList>
      )}
    </MeWrap>
  );
}

function MeSection({ meView, meetings, gcalStatus, onConnect }) {
  const [range, setRange] = useState("today");
  const [meDayCursor, setMeDayCursor] = useState(() => atMidnight(new Date())); // picked day (meetings)
  const [meetMonthCursor, setMeetMonthCursor] = useState(() => new Date()); // meetings month grid
  const [meMonthCursor, setMeMonthCursor] = useState(() => new Date());
  const viewMonth = meMonthCursor.getMonth(); // 0-11
  const viewYear = meMonthCursor.getFullYear();
  const isCurrentMonth = viewMonth === new Date().getMonth() && viewYear === new Date().getFullYear();
  const monthEvents = useMemo(() => {
    try {
      return getAllEvents().filter((e) => e.month === viewMonth + 1).sort((a, b) => a.day - b.day);
    } catch {
      return [];
    }
  }, [viewMonth]);
  const goMonth = (dir) => setMeMonthCursor((c) => new Date(c.getFullYear(), c.getMonth() + dir, 1));
  if (meView === "meetings") {
    return (
      <>
        {gcalStatus === "connected" ? (
          <>
            <MeToolbar>
              <Segmented>
                <Seg $on={range === "today"} onClick={() => setRange("today")}>היום</Seg>
                <Seg $on={range === "tomorrow"} onClick={() => setRange("tomorrow")}>מחר</Seg>
                <Seg $on={range === "week"} onClick={() => setRange("week")}>שבוע</Seg>
                <Seg $on={range === "month"} onClick={() => setRange("month")}>חודש</Seg>
              </Segmented>
            </MeToolbar>
            <MeetingsView
              meetings={meetings}
              range={range}
              dayCursor={meDayCursor}
              monthCursor={meetMonthCursor}
              onPickDay={(dt) => { setMeDayCursor(atMidnight(dt)); setRange("day"); }}
              onMonthNav={(dir) => setMeetMonthCursor((c) => new Date(c.getFullYear(), c.getMonth() + dir, 1))}
            />
          </>
        ) : (
          <MeWrap>
            {gcalStatus === "need-setup" ? (
              <MeEmpty>נדרש להפעיל את Google Calendar API בפרויקט (אדריך).</MeEmpty>
            ) : (
              <MeConnectBtn onClick={onConnect} disabled={gcalStatus === "connecting"}>
                {gcalStatus === "connecting" ? "מתחבר…" : "התחברות עם Google ליומן"}
              </MeConnectBtn>
            )}
          </MeWrap>
        )}
      </>
    );
  }
  return (
    <MeWrap>
      <NavBar style={{ marginTop: 0 }}>
        <NavGroup>
          <NavArrow onClick={() => goMonth(-1)} title="חודש קודם">‹</NavArrow>
          <RangeLabel dir="ltr" onClick={() => setMeMonthCursor(new Date())} title="חזרה לחודש הנוכחי">
            {HE_MONTHS[viewMonth]} {viewYear}
          </RangeLabel>
          <NavArrow onClick={() => goMonth(1)} title="חודש הבא">›</NavArrow>
        </NavGroup>
      </NavBar>
      {monthEvents.length === 0 ? (
        <MeEmpty>אין אירועים בחודש זה 🎉</MeEmpty>
      ) : (
        <MeList>
          {monthEvents.map((e, i) => {
            const disp = new Date(viewYear, e.month - 1, e.day);
            return (
              <ListRow key={i} $accent="#B36BFF" style={{ animationDelay: `${i * 40}ms` }}>
                <ListDate>
                  <b>{HE_DAYS_SHORT[disp.getDay()]}</b>
                  <small>{fmtDM(disp)}</small>
                </ListDate>
                <ListBody>
                  {isCurrentMonth && e.diffDays === 0 ? <EvTag $exam>היום</EvTag> : isCurrentMonth && e.diffDays === 1 ? <EvTag>מחר</EvTag> : null}
                  <ListText>{meEventTitle(e)}</ListText>
                </ListBody>
              </ListRow>
            );
          })}
        </MeList>
      )}
    </MeWrap>
  );
}

function MailView({ mail }) {
  if (!mail || !Array.isArray(mail.items)) {
    return <ListEmpty>אין דואר עדיין. נמשך אוטומטית ממשו״ב בכל פתיחה.</ListEmpty>;
  }
  const items = mail.items.slice();
  if (items.length === 0) return <ListEmpty>אין הודעות בתיבה 📭</ListEmpty>;
  return (
    <ListWrap>
      {items.map((m, i) => {
        const d = m.date && /^\d{4}-\d{2}-\d{2}$/.test(m.date) ? new Date(m.date + "T00:00") : null;
        return (
          <ListRow key={i} $accent={m.isNew ? "#FF6478" : "#5aa0ff"} style={{ animationDelay: `${i * 40}ms` }}>
            <ListDate>
              {d ? (<><b>{HE_DAYS_SHORT[d.getDay()]}</b><small>{fmtDM(d)}</small></>) : <small>—</small>}
            </ListDate>
            <ListBody>
              {m.isNew && <EvTag $exam>חדש</EvTag>}
              <div>
                <ListText>{m.subject || "(ללא נושא)"}</ListText>
                {m.from && <MailFrom>{m.from}</MailFrom>}
              </div>
            </ListBody>
          </ListRow>
        );
      })}
    </ListWrap>
  );
}

const POSITIVE_RE = /(מילה טובה|שבח|הצטיינות|הצטיין|חיזוק|מצטיין|כל הכבוד|יפה מאוד|למופת|ציון לשבח)/;
function ListView({ items, kind, who }) {
  const subjectOf = (t) => String(t || "").split(" — ")[0].trim();
  const learnMore = (t) => {
    const q = `הסבר לי בצורה פשוטה וברורה, עם דוגמה ותרגיל אחד עם פתרון: ${t}`;
    window.open("https://chatgpt.com/?q=" + encodeURIComponent(q), "_blank", "noopener");
  };
  const brag = (t) => {
    const msg = `🎉👏 מילה טובה${who ? " ל" + who : ""}! ${subjectOf(t)}`;
    window.open("https://wa.me/?text=" + encodeURIComponent(msg), "_blank", "noopener");
  };
  if (!Array.isArray(items)) {
    return <ListEmpty>אין נתונים עדיין. משכו ממשו״ב (⟳ או ״יבוא ממשו״ב״).</ListEmpty>;
  }
  const todayY = ymd(new Date());
  let show = items.slice().filter((e) => e && e.date);
  if (kind === "homework") {
    // upcoming (soonest first), then recently-given homework (newest first)
    const future = show.filter((e) => e.date >= todayY).sort((a, b) => a.date.localeCompare(b.date));
    const past = show.filter((e) => e.date < todayY).sort((a, b) => b.date.localeCompare(a.date));
    show = [...future, ...past];
  } else show = show.sort((a, b) => b.date.localeCompare(a.date)); // behavior: newest first
  if (show.length === 0) {
    return <ListEmpty>{kind === "homework" ? "אין מטלות או מבחנים 🎉" : "אין רשומות התנהגות 👍"}</ListEmpty>;
  }
  return (
    <ListWrap>
      {show.map((e, i) => {
        const d = new Date(e.date + "T00:00");
        const isExam = kind === "homework" && e.type === "exam";
        const isPositive = kind === "behavior" && POSITIVE_RE.test(e.text || "");
        return (
          <ListRow key={i} $accent={isExam ? "#FF6478" : isPositive ? "#57c785" : kind === "behavior" ? "#ff9db0" : "#5aa0ff"} style={{ animationDelay: `${i * 40}ms` }}>
            <ListDate>
              <b>{HE_DAYS_SHORT[d.getDay()]}</b>
              <small>{fmtDM(d)}</small>
            </ListDate>
            <ListBody>
              {isExam && <EvTag $exam>מבחן</EvTag>}
              <ListText>{e.text}</ListText>
            </ListBody>
            {kind === "homework" && (
              <ListAction onClick={() => learnMore(e.text)} title="למד עוד עם ChatGPT">✨ למד עוד</ListAction>
            )}
            {isPositive && (
              <ListAction $wa onClick={() => brag(e.text)} title="שלח בוואטסאפ">📣 שווץ</ListAction>
            )}
          </ListRow>
        );
      })}
    </ListWrap>
  );
}

/* ============================================================
   Week grid
   ============================================================ */
function WeekGrid({ weekStart, rows, cellFor, holidaysFor, eventsFor, today, onEdit, onEditRows, onPickDay }) {
  const dates = DAYS.map((d, i) => addDays(weekStart, i));
  return (
    <GridWrap>
      <Grid $rows={rows.length}>
        <Corner onClick={onEditRows} title="עריכת שעות" style={{ cursor: "pointer" }}>שיעור</Corner>
        {DAYS.map((d, i) => {
          const date = dates[i];
          const hol = holidaysFor(date);
          const ev = eventsFor ? eventsFor(date) : [];
          const isToday = sameDay(date, today);
          return (
            <HeadCell key={d.key} $today={isToday} onClick={() => onPickDay(date)} title={`מעבר ליום ${d.name}`}>
              <Hd>{d.name}</Hd>
              <HdDate>{fmtDM(date)}</HdDate>
              {hol[0] && <HdHol title={hol.join(" · ")}>🎉 {hol[0]}</HdHol>}
              {ev.length > 0 && <HdEv title={ev.map((e) => e.text).join(" · ")}>📝 {ev.length}</HdEv>}
            </HeadCell>
          );
        })}
        {rows.map((r) => (
          <React.Fragment key={r.id}>
            <RowLabel onClick={onEditRows} title="עריכת שעות">
              <RowNum>{r.title}</RowNum>
              <TimeStack time={r.time} />
            </RowLabel>
            {DAYS.map((d) => {
              const c = cellFor(r.id, d.key);
              return (
                <GCell
                  key={d.key}
                  $bg={c && c.color}
                  onClick={() => onEdit(r.id, d.key)}
                  style={c && c.color ? { color: textOn(c.color) } : undefined}
                >
                  {c && c.text ? (
                    <GText>
                      {c.text}
                      {c.teacher && <GTeacher>{c.teacher}</GTeacher>}
                    </GText>
                  ) : (
                    <Plus>＋</Plus>
                  )}
                </GCell>
              );
            })}
          </React.Fragment>
        ))}
      </Grid>
    </GridWrap>
  );
}

/* ============================================================
   Day view
   ============================================================ */
function DayView({ date, rows, cellFor, holidays, events, isToday, isTomorrow, tomorrowRain, isMashov, onEdit, onEditRows }) {
  const dow = date.getDay(); // 0..6
  const dayKey = String(dow);
  const isSchoolDay = dow >= 0 && dow <= 5;

  // gaps between a lesson's end and the next lesson's start → break bars
  const gaps = {};
  for (let i = 0; i < rows.length - 1; i++) {
    const p = parseRange(rows[i].time);
    const n = parseRange(rows[i + 1].time);
    if (p && n && n.s - p.e >= 5) gaps[rows[i].id] = n.s - p.e;
  }
  // any PE / sports lesson today → remind to bring sports clothes
  const hasSport = isSchoolDay && rows.some((r) => {
    const c = cellFor(r.id, dayKey);
    return c && c.text && SPORT_RE.test(c.text);
  });
  const hasAnyLesson = isSchoolDay && rows.some((r) => {
    const c = cellFor(r.id, dayKey);
    return c && c.text;
  });
  return (
    <DayWrap>
      <DayHead $today={isToday}>
        <DayName>
          {HE_DAYS[dow]}
          {isToday && <TodayDot>היום</TodayDot>}
          {isTomorrow && <TodayDot>מחר</TodayDot>}
        </DayName>
        <DayDate>{fmtDMY(date)}</DayDate>
      </DayHead>

      {holidays[0] && (
        <HolBanner>
          <span>🎉</span>
          <div>
            {holidays.map((h, i) => (
              <b key={i}>{h}</b>
            ))}
          </div>
        </HolBanner>
      )}

      {tomorrowRain && (
        <RainBanner>
          <span>☔</span>
          <div>
            <b>מחר יירד גשם</b>
          </div>
        </RainBanner>
      )}

      {hasSport && (
        <SportBanner>
          <span>🎽</span>
          <div>
            <b>לא לשכוח בגדי ספורט</b>
          </div>
        </SportBanner>
      )}

      {events && events.length > 0 && (
        <EventsBox>
          {events.map((e, i) => (
            <EventRow key={i} $exam={e.type === "exam"}>
              <EvTag $exam={e.type === "exam"}>{e.type === "exam" ? "מבחן" : "מטלה"}</EvTag>
              <EvText>{e.text}</EvText>
            </EventRow>
          ))}
        </EventsBox>
      )}

      {!isSchoolDay ? (
        <RestCard>
          <RestEmoji>🌙</RestEmoji>
          <RestText>שבת — אין לימודים</RestText>
        </RestCard>
      ) : !hasAnyLesson && (isMashov || holidays[0]) ? (
        <RestCard onClick={() => rows[0] && onEdit(rows[0].id, dayKey)} style={{ cursor: rows[0] ? "pointer" : "default" }}>
          <RestEmoji>🎈</RestEmoji>
          <RestText>אין לימודים היום</RestText>
          {rows[0] && <RestHint>＋ הקשה להוספת שיעור</RestHint>}
        </RestCard>
      ) : (
        <DayList>
          {rows.map((r, ri) => {
            const c = cellFor(r.id, dayKey);
            return (
              <React.Fragment key={r.id}>
                <DayRow style={{ animationDelay: `${ri * 45}ms` }}>
                  <DayRowTime onClick={onEditRows} title="עריכת שעות">
                    <RowNum>{r.title}</RowNum>
                    <TimeStack time={r.time} />
                  </DayRowTime>
                  <DayRowBody
                    onClick={() => onEdit(r.id, dayKey)}
                    $bg={c && c.color}
                    style={c && c.color ? { color: textOn(c.color) } : undefined}
                  >
                    {c && c.text ? (
                      <DayRowText>
                        {c.text}
                        {c.teacher && <DayRowTeacher>{c.teacher}</DayRowTeacher>}
                      </DayRowText>
                    ) : (
                      <DayRowEmpty>＋ הוסף</DayRowEmpty>
                    )}
                  </DayRowBody>
                </DayRow>
                {gaps[r.id] != null && <BreakStrip minutes={gaps[r.id]} />}
              </React.Fragment>
            );
          })}
        </DayList>
      )}
    </DayWrap>
  );
}

function BreakStrip({ minutes }) {
  return (
    <BreakBar>
      <BreakLabel>
        <span>☕ הפסקה · {minutes} דק׳</span>
      </BreakLabel>
    </BreakBar>
  );
}

/* ============================================================
   Month view
   ============================================================ */
function MonthView({ cursor, rows, cellFor, holidaysFor, eventsFor, today, onPick }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const month = cursor.getMonth();

  const scheduledDays = useMemo(() => {
    const s = new Set();
    DAYS.forEach((d) => {
      if (rows.some((r) => (cellFor(r.id, d.key) || {}).text)) s.add(Number(d.key));
    });
    return s;
  }, [rows, cellFor]);

  return (
    <MonthWrap>
      <MonthHead>
        {HE_DAYS_SHORT.map((s, i) => (
          <MHd key={i} $weekend={i === 6}>
            {s}
          </MHd>
        ))}
      </MonthHead>
      <MonthGrid>
        {cells.map((date, i) => {
          const inMonth = date.getMonth() === month;
          const hol = holidaysFor(date);
          const dow = date.getDay();
          const isToday = sameDay(date, today);
          const ev = eventsFor ? eventsFor(date) : [];
          const hasExam = ev.some((e) => e.type === "exam");
          const hasSchedule = inMonth && dow <= 5 && scheduledDays.has(dow) && hol.length === 0;
          return (
            <MCell key={i} $in={inMonth} $today={isToday} $weekend={dow === 6} onClick={() => onPick(date)}>
              <MNum $today={isToday}>{date.getDate()}</MNum>
              {hol[0] && (
                <MHol title={hol.join(" · ")}>
                  {hol[0].length > 12 ? hol[0].slice(0, 11) + "…" : hol[0]}
                </MHol>
              )}
              {ev.length > 0 && (
                <MEv $exam={hasExam} title={ev.map((e) => e.text).join(" · ")}>
                  📝 {ev.length}
                </MEv>
              )}
              {hasSchedule && <MDot />}
            </MCell>
          );
        })}
      </MonthGrid>
      <MonthLegend>
        <Lg>
          <MDot /> יום לימודים
        </Lg>
        <Lg>🎉 חג / חופשה</Lg>
        <Lg>📝 מבחן / מטלה</Lg>
      </MonthLegend>
    </MonthWrap>
  );
}

/* ============================================================
   Weather sheet
   ============================================================ */
function WeatherSheet({ weather, holidaysFor, onClose }) {
  const ok = weather.status === "ok";
  const cur = ok ? weather.current : null;
  const [cEmoji, cLabel] = cur ? wmo(cur.code) : ["🌡️", ""];
  return (
    <Sheet onClose={onClose} title="מזג האוויר" sub={weather.place || ""}>
      {!ok ? (
        <WxLoading>{weather.status === "loading" ? "טוען תחזית…" : "לא הצלחתי לטעון תחזית."}</WxLoading>
      ) : (
        <>
          <WxNow>
            <WxNowEmoji>{cEmoji}</WxNowEmoji>
            <div>
              <WxNowTemp>{cur.temp}°</WxNowTemp>
              <WxNowLabel>{cLabel}</WxNowLabel>
            </div>
          </WxNow>
          <WxList>
            {weather.daily.map((d, i) => {
              const dt = new Date(d.date + "T00:00");
              const [em] = wmo(d.code);
              const hol = holidaysFor(dt);
              return (
                <WxRow key={d.date}>
                  <WxDay>
                    {i === 0 ? "היום" : HE_DAYS[dt.getDay()]}
                    <WxDate>{fmtDM(dt)}</WxDate>
                  </WxDay>
                  <WxEmoji>{em}</WxEmoji>
                  {hol[0] ? <WxHol>🎉 {hol[0]}</WxHol> : <WxHol />}
                  <WxTemps>
                    <WxMax>{d.max}°</WxMax>
                    <WxMin>{d.min}°</WxMin>
                  </WxTemps>
                </WxRow>
              );
            })}
          </WxList>
        </>
      )}
    </Sheet>
  );
}

function fmtNewsTime(str) {
  const d = new Date(str);
  if (isNaN(d)) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return sameDay ? `${hh}:${mm}` : `${fmtDM(d)} · ${hh}:${mm}`;
}
function NewsSheet({ news, onClose }) {
  const ok = news.status === "ok" && news.items.length > 0;
  return (
    <Sheet onClose={onClose} title="חדשות" sub="כותרות אחרונות · ynet">
      {!ok ? (
        <WxLoading>{news.status === "loading" ? "טוען חדשות…" : "לא הצלחתי לטעון חדשות."}</WxLoading>
      ) : (
        <NewsList>
          {news.items.map((it, i) => (
            <NewsItem key={i} href={it.link || "#"} target="_blank" rel="noopener noreferrer">
              <NewsText>{it.title}</NewsText>
              {it.date && <NewsTime>{fmtNewsTime(it.date)}</NewsTime>}
            </NewsItem>
          ))}
        </NewsList>
      )}
    </Sheet>
  );
}

/* ============================================================
   Cell editor
   ============================================================ */
function CellEditor({ value, rowLabel, dayLabel, onClose, onSave, onClear }) {
  const [text, setText] = useState(value ? value.text || "" : "");
  const [color, setColor] = useState(value ? value.color || "" : "");
  const inputRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 120);
    return () => clearTimeout(t);
  }, []);
  return (
    <Sheet onClose={onClose} title={dayLabel} sub={rowLabel}>
      <Field
        ref={inputRef}
        value={text}
        placeholder="שם הפעילות / השיעור"
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSave({ text: text.trim(), color })}
      />
      <SwatchLabel>צבע</SwatchLabel>
      <Swatches>
        <SwatchNone $on={!color} onClick={() => setColor("")} title="ללא">✕</SwatchNone>
        {SWATCHES.map((c) => (
          <Swatch key={c} $c={c} $on={color === c} onClick={() => setColor(c)} />
        ))}
      </Swatches>
      <SheetActions>
        <BigBtn onClick={() => onSave({ text: text.trim(), color })}>שמור</BigBtn>
        {value && (
          <GhostBtn $danger onClick={onClear}>
            מחק
          </GhostBtn>
        )}
      </SheetActions>
    </Sheet>
  );
}

/* ============================================================
   Rows editor
   ============================================================ */
function RowsEditor({ rows, onClose, onSave }) {
  const [draft, setDraft] = useState(() => rows.map((r) => ({ ...r })));
  const upd = (i, patch) => setDraft((d) => d.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const del = (i) => setDraft((d) => d.filter((_, idx) => idx !== i));
  const add = () => setDraft((d) => [...d, { id: uid7(), title: String(d.length + 1), time: "" }]);
  const move = (i, dir) =>
    setDraft((d) => {
      const j = i + dir;
      if (j < 0 || j >= d.length) return d;
      const next = d.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  return (
    <Sheet onClose={onClose} title="עריכת שעות" sub="חל על הלוח הנוכחי בלבד">
      <RowsList>
        {draft.map((r, i) => (
          <REditRow key={r.id}>
            <REorder>
              <MiniBtn onClick={() => move(i, -1)} disabled={i === 0}>↑</MiniBtn>
              <MiniBtn onClick={() => move(i, 1)} disabled={i === draft.length - 1}>↓</MiniBtn>
            </REorder>
            <RTitleIn value={r.title} placeholder="#" onChange={(e) => upd(i, { title: e.target.value })} />
            <RTimeIn value={r.time} placeholder="08:00–08:50" onChange={(e) => upd(i, { time: e.target.value })} />
            <MiniBtn $danger onClick={() => del(i)}>✕</MiniBtn>
          </REditRow>
        ))}
      </RowsList>
      <GhostBtn onClick={add}>＋ הוסף שורה</GhostBtn>
      <SheetActions>
        <BigBtn
          onClick={() => {
            onSave(draft.filter((r) => (r.title || "").trim() || (r.time || "").trim()));
            onClose();
          }}
        >
          שמור
        </BigBtn>
      </SheetActions>
    </Sheet>
  );
}

/* ============================================================
   Name prompt
   ============================================================ */
function NamePromptModal({ mode, initial, connectedMashov, onClose, onSubmit, onFromImage, onReplaceFromImage, onFromMashov, onDelete }) {
  const [name, setName] = useState(initial || "");
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmImg, setConfirmImg] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 120);
    return () => clearTimeout(t);
  }, []);
  return (
    <Sheet onClose={onClose} title={mode === "create" ? "לוח חדש" : "עריכת לוח"}>
      <Field
        ref={inputRef}
        value={name}
        placeholder="שם הלוח (למשל: כפיר, רותם, שבוע א׳)"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && name.trim() && onSubmit(name.trim())}
      />
      <SheetActions>
        <BigBtn disabled={!name.trim()} onClick={() => name.trim() && onSubmit(name.trim())}>
          {mode === "create" ? "צור" : "שמור"}
        </BigBtn>
        {mode === "create" && onFromImage && (
          <GhostBtn onClick={onFromImage}>
            <CamIcon src="/cam-icon.png" alt="" />מתוך תמונה
          </GhostBtn>
        )}
        {mode === "rename" && onReplaceFromImage && (
          <GhostBtn
            onClick={() => (confirmImg ? onReplaceFromImage() : setConfirmImg(true))}
            style={confirmImg ? { background: "#3a2e10", borderColor: "#8a6d1a", color: "#ffd27a" } : undefined}
          >
            {confirmImg ? "יחליף הכל · שוב" : (<><CamIcon src="/cam-icon.png" alt="" />מתוך תמונה</>)}
          </GhostBtn>
        )}
      </SheetActions>
      {onFromMashov && (
        <GhostBtn onClick={onFromMashov} style={{ width: "100%", marginTop: 10 }}>
          {connectedMashov ? "שינוי פרטי התחברות ממשו״ב" : "משיכה ממשו״ב"}
        </GhostBtn>
      )}
      {mode === "rename" && connectedMashov ? (
        <ConnectedNote>✓ לוח זה מחובר למשו״ב</ConnectedNote>
      ) : mode === "rename" && onReplaceFromImage ? (
        <WarnNote>⚠️ ״מתוך תמונה״ / ״משו״ב״ יחליפו את כל השיעורים והשעות בלוח זה.</WarnNote>
      ) : null}
      {mode === "rename" && onDelete && (
        <GhostBtn
          $danger
          onClick={() => (confirmDel ? onDelete() : setConfirmDel(true))}
          style={{ width: "100%", marginTop: 10, background: confirmDel ? "#3a1620" : "transparent" }}
        >
          {confirmDel ? "בטוחים? לחצו שוב למחיקה" : "מחק לוח"}
        </GhostBtn>
      )}
    </Sheet>
  );
}

/* ============================================================
   Reminders sheet (Web Push)
   ============================================================ */
function RemindersSheet({ settings, supported, onClose, onSave, onTest }) {
  const [enabled, setEnabled] = useState(!!settings.enabled);
  const [morning, setMorning] = useState(settings.morning !== false);
  const [morningHm, setMorningHm] = useState(settings.morningHm || "07:00");
  const [events, setEvents] = useState(settings.events !== false);
  const [eventHm, setEventHm] = useState(settings.eventHm || "18:00");
  return (
    <Sheet onClose={onClose} title="תזכורות" sub="תזכורת בוקר על לוח היום, ותזכורת ערב לפני מטלות ומבחנים">
      {!supported && <RemNote>⚠️ ב-iPhone צריך קודם להוסיף את האפליקציה למסך הבית, ואז לפתוח מכאן ולאשר התראות.</RemNote>}
      <RemRow onClick={() => setEnabled((e) => !e)}>
        <RemLabel>הפעלת תזכורות</RemLabel>
        <Toggle $on={enabled} role="switch" aria-checked={enabled}>
          <ToggleKnob $on={enabled} />
        </Toggle>
      </RemRow>
      <RemSection style={enabled ? undefined : { opacity: 0.4, pointerEvents: "none" }}>
        <RemRow onClick={() => setMorning((e) => !e)}>
          <RemLabel>תזכורת בוקר <RemHint>לוח היום</RemHint></RemLabel>
          <Toggle $on={morning} role="switch" aria-checked={morning}>
            <ToggleKnob $on={morning} />
          </Toggle>
        </RemRow>
        {morning && (
          <ChipRow>
            {["06:45", "07:00", "07:15", "07:30"].map((t) => (
              <PickChip key={t} $on={morningHm === t} onClick={() => setMorningHm(t)}>{t}</PickChip>
            ))}
          </ChipRow>
        )}
        <RemRow onClick={() => setEvents((e) => !e)} style={{ marginTop: 18 }}>
          <RemLabel>מטלות ומבחנים <RemHint>ערב לפני</RemHint></RemLabel>
          <Toggle $on={events} role="switch" aria-checked={events}>
            <ToggleKnob $on={events} />
          </Toggle>
        </RemRow>
        {events && (
          <ChipRow>
            {["17:00", "18:00", "19:00", "20:00"].map((t) => (
              <PickChip key={t} $on={eventHm === t} onClick={() => setEventHm(t)}>{t}</PickChip>
            ))}
          </ChipRow>
        )}
      </RemSection>
      <SheetActions>
        <BigBtn onClick={() => onSave({ enabled, morning, morningHm, events, eventHm })}>שמור</BigBtn>
        {enabled && <GhostBtn onClick={onTest}>בדיקה 🔔</GhostBtn>}
      </SheetActions>
    </Sheet>
  );
}

/* ============================================================
   Mashov (משו"ב) connect sheet
   ============================================================ */
function MashovSheet({ initial, onClose, onSubmit }) {
  const now = new Date();
  const defYear = now.getMonth() >= 7 ? now.getFullYear() + 1 : now.getFullYear();
  const [semel, setSemel] = useState(initial && initial.semel ? String(initial.semel) : "");
  const [schoolName, setSchoolName] = useState((initial && initial.schoolName) || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);
  const [year, setYear] = useState(initial && initial.year ? String(initial.year) : String(defYear));
  const [username, setUsername] = useState((initial && initial.username) || "");
  const [password, setPassword] = useState((initial && initial.password) || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onSchoolInput = (v) => {
    setSchoolName(v);
    setSemel("");
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (v.trim().length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${MASHOV_ENDPOINT}/schools?q=${encodeURIComponent(v.trim())}`);
        const j = await r.json();
        setResults(Array.isArray(j) ? j : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };
  const pickSchool = (s) => {
    setSemel(String(s.semel));
    setSchoolName(s.name);
    setResults([]);
  };

  const submit = async () => {
    if (!semel) {
      setErr("בחרו בית ספר מהרשימה");
      return;
    }
    if (!year || !username || !password) {
      setErr("יש למלא את כל השדות");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await onSubmit({ semel: Number(semel), schoolName, year: Number(year), username: username.trim(), password });
    } catch (e) {
      setErr(e && e.message ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={onClose} title="משיכה ממשו״ב" sub="התחברות לחשבון משו״ב שלך">
      <RemSub>בית הספר</RemSub>
      <SchoolWrap>
        <Field value={schoolName} placeholder="הקלד שם בית ספר…" onChange={(e) => onSchoolInput(e.target.value)} />
        {(searching || results.length > 0) && !semel && (
          <SchoolResults>
            {searching && <SchoolHint>מחפש…</SchoolHint>}
            {results.map((s) => (
              <SchoolItem key={s.semel} onClick={() => pickSchool(s)}>
                <span>{s.name}</span>
                <small>{s.semel}</small>
              </SchoolItem>
            ))}
            {!searching && results.length === 0 && <SchoolHint>לא נמצאו בתי ספר</SchoolHint>}
          </SchoolResults>
        )}
      </SchoolWrap>
      {semel && <Picked>✓ נבחר · סמל {semel}</Picked>}
      <RemSub>שנת לימודים</RemSub>
      <Field type="number" value={year} onChange={(e) => setYear(e.target.value)} />
      <RemSub>שם משתמש / ת״ז</RemSub>
      <Field value={username} placeholder="שם משתמש" onChange={(e) => setUsername(e.target.value)} />
      <RemSub style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <span>סיסמה</span>
        <PassHelp
          href={`https://web.mashov.info/students/recover?semel=${encodeURIComponent(semel || "")}&username=${encodeURIComponent(username || "")}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          לא יודעת מה הסיסמה?
        </PassHelp>
      </RemSub>
      <Field type="password" value={password} placeholder="סיסמה" onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
      {err && <WarnNote style={{ color: "#FF6478", borderColor: "#5a2630", background: "rgba(255,46,99,.06)" }}>{err}</WarnNote>}
      <WarnNote>🔒 הפרטים נשמרים בחשבון הפרטי שלך (מוגן) כדי לאפשר משיכה בלחיצה. יעדכן את השיעורים בלוח.</WarnNote>
      <SheetActions>
        <BigBtn disabled={busy} onClick={submit}>{busy ? "מתחבר ומושך…" : "משוך מערכת"}</BigBtn>
      </SheetActions>
    </Sheet>
  );
}

/* ============================================================
   Generic sheet
   ============================================================ */
function Sheet({ title, sub, children, onClose }) {
  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <SheetBox onClick={(e) => e.stopPropagation()}>
        <SheetHead>
          <div>
            <SheetTitle>{title}</SheetTitle>
            {sub && <SheetSub>{sub}</SheetSub>}
          </div>
          <IconBtn onClick={onClose}>✕</IconBtn>
        </SheetHead>
        {children}
      </SheetBox>
    </Overlay>
  );
}

/* ============================================================
   Styles
   ============================================================ */
const INK = "#0a0a0a";
const BONE = "#f2ece0";
const ACID = "#D7FF3E";

const fade = keyframes`from{opacity:0}to{opacity:1}`;
const rise = keyframes`from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}`;
// forward wave: each opaque tile flashes green, then back to dark (still covering)
const cellFlash = keyframes`
  0% { background-color: ${INK}; }
  45% { background-color: var(--flash); }
  100% { background-color: ${INK}; }
`;
// return wave: each tile lights up to a bright opaque crest, then fades away —
// the disappearing green tiles are what reveals the content behind them (diagonal wipe up)
const cellReveal = keyframes`
  0%   { background-color: ${INK};       opacity: 1; }
  35%  { background-color: var(--flash); opacity: 1; }
  55%  { background-color: var(--flash); opacity: 1; }
  100% { background-color: var(--flash); opacity: 0; }
`;
// fields enter with a quick slide from the (visual) left (used on view/child switches)
const slideLeft = keyframes`from{opacity:0;transform:translateX(-22px)} to{opacity:1;transform:none}`;
const ViewFade = styled.div`
  opacity: ${(p) => (p.$show ? 1 : 0)};
  transform: ${(p) => (p.$show ? "none" : "translateY(12px)")};
  transition: opacity .3s ease, transform .3s cubic-bezier(.22,1,.36,1);
`;
const SplashWrap = styled.div`
  position: fixed; inset: 0; z-index: 9999; display: grid; gap: 0; overflow: hidden;
  background: transparent; pointer-events: ${(p) => (p.$gone ? "none" : "auto")};
`;
const SplashCell = styled.span`
  opacity: 1; background-color: ${INK};
  animation-name: ${cellFlash}, ${cellReveal};
  animation-timing-function: ease, cubic-bezier(.4, 0, .2, 1);
  animation-fill-mode: both, forwards;
  animation-duration: var(--fd), var(--rd);
  animation-delay: var(--f), var(--b);
`;
const spin = keyframes`to { transform: rotate(360deg); }`;

const Global = createGlobalStyle`
  html, body, #root { height: 100%; }
  body { background: ${INK}; margin: 0; }
  #root * { box-sizing: border-box; }
  * { -webkit-tap-highlight-color: transparent; }
`;
const Shell = styled.div`
  min-height: 100%;
  background:
    radial-gradient(120% 60% at 100% -10%, rgba(215,255,62,.08), transparent 55%),
    radial-gradient(120% 60% at 0% 0%, rgba(44,107,255,.07), transparent 55%),
    ${INK};
  color: ${BONE};
  font-family: "Heebo", "Assistant", system-ui, sans-serif;
  padding: env(safe-area-inset-top) 0 env(safe-area-inset-bottom);
  animation: ${fade} .3s ease;
`;
const Loading = styled.div`
  min-height: 60vh; display: grid; place-items: center; font-weight: 800; letter-spacing: .5px; color: #7c776c;
`;
const DbError = styled.div`
  max-width: 520px; margin: 40px auto; padding: 26px 20px; text-align: center; border: 1px solid #5a2630;
  background: rgba(255,46,99,.06);
`;
const DbErrTitle = styled.div`font-size: 20px; font-weight: 900; color: #FF6478;`;
const DbErrText = styled.div`font-size: 14px; font-weight: 600; color: #a8a293; margin: 10px 0 12px; line-height: 1.6;`;
const DbErrCode = styled.code`
  display: block; direction: ltr; font-size: 13px; font-weight: 700; color: ${ACID}; background: #0e0d09;
  border: 1px solid #2c2b26; padding: 10px 12px; user-select: all;
`;
const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; text-align: center;
  padding: 40px 22px 60px; max-width: 460px; margin: 0 auto; animation: ${fade} .3s ease;
`;
const EmptyTitle = styled.div`font-size: clamp(28px, 7vw, 44px); font-weight: 900; letter-spacing: -1px;`;
const EmptyText = styled.div`
  font-size: 17px; font-weight: 600; color: #a8a293; line-height: 1.7; margin-top: 8px;
  b { color: ${ACID}; }
`;
const EmptyBtns = styled.div`display: flex; flex-direction: column; gap: 10px; width: 100%; margin-top: 22px;`;

/* auth gate */
const Gate = styled.div`
  min-height: 100vh; display: flex; flex-direction: column; justify-content: center;
  padding: 40px 26px; max-width: 560px; margin: 0 auto;
`;
const GateTag = styled.div`
  font-size: 12px; font-weight: 800; letter-spacing: 4px; color: ${ACID};
  text-transform: uppercase; margin-bottom: 20px; direction: ltr; text-align: right;
`;
const GateTitle = styled.h1`
  margin: 0; font-weight: 900; line-height: .86; font-size: clamp(76px, 28vw, 168px); letter-spacing: -3px; color: ${BONE};
`;
const GateSub = styled.p`font-size: 17px; font-weight: 600; line-height: 1.7; color: #a8a293; margin: 26px 0 34px; max-width: 30ch;`;
const GBtn = styled.button`
  align-self: flex-start; display: inline-flex; align-items: center; gap: 12px; border: none; cursor: pointer;
  font-family: inherit; font-size: 18px; font-weight: 900; color: ${INK}; background: ${ACID}; padding: 16px 34px;
  transition: transform .08s, filter .15s;
  &:hover:not(:disabled){ filter: brightness(1.05); } &:active{ transform: translateY(2px); } &:disabled{ opacity:.6; cursor:not-allowed; }
`;
const GG = styled.span`font-family: Arial, sans-serif; font-weight: 900; font-size: 20px; color: ${INK};`;
const GateFoot = styled.div`margin-top: 48px; font-size: 12px; font-weight: 700; letter-spacing: 2px; color: #5a564d;`;

/* masthead */
const Masthead = styled.header`
  display: flex; flex-direction: column; gap: 8px; padding: 14px 16px 12px;
  border-bottom: 1px solid #201f1b; max-width: 1180px; margin: 0 auto;
`;
const MastMeta = styled.div`display: flex; align-items: center; gap: 10px; flex-wrap: nowrap; overflow: hidden;`;
const Tag = styled.div`
  font-size: 11px; font-weight: 800; letter-spacing: .5px; color: ${ACID}; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis; min-width: 0;
`;
const MastMain = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px;`;
const BrandWrap = styled.div`position: relative; min-width: 0;`;
const Brand = styled.button`
  border: none; background: transparent; cursor: pointer; font-family: inherit; color: ${BONE};
  font-size: clamp(26px, 8vw, 40px); font-weight: 900; letter-spacing: -1.5px; line-height: 1;
  white-space: nowrap; display: inline-flex; align-items: center; gap: 8px; padding: 0;
`;
const Tri = styled.span`font-size: .5em; color: ${ACID}; line-height: 1;`;
const ViewMenu = styled.div`
  position: absolute; top: calc(100% + 8px); inset-inline-start: 0; z-index: 41; min-width: 210px;
  background: #16150f; border: 1px solid #2c2b26; padding: 6px; display: flex; flex-direction: column; gap: 2px;
  box-shadow: 0 12px 30px rgba(0,0,0,.5);
`;
const ViewItem = styled.button`
  text-align: right; border: none; cursor: pointer; font-family: inherit; font-size: 16px; font-weight: 800;
  padding: 11px 12px; background: ${(p) => (p.$on ? ACID : "transparent")}; color: ${(p) => (p.$on ? INK : BONE)};
  &:hover{ background: ${(p) => (p.$on ? ACID : "#201f18")}; color: ${(p) => (p.$on ? INK : ACID)}; }
`;
const MastControls = styled.div`display: flex; align-items: center; gap: 10px; flex: 0 0 auto;`;
const WeatherChip = styled.button`
  display: inline-flex; align-items: center; gap: 5px; cursor: pointer; font-family: inherit;
  border: 1px solid #2c2b26; background: transparent; color: ${BONE}; padding: 6px 11px; height: 34px;
  span{ font-size: 16px; line-height: 1; } b{ font-weight: 900; font-size: 14px; }
  &:hover{ border-color: ${ACID}; }
`;
const Ava = styled.img`width: 34px; height: 34px; object-fit: cover; border: 1px solid #2c2b26;`;
const AvaX = styled.div`width: 34px; height: 34px; display: grid; place-items: center; background: #1a1915; font-size: 16px;`;
const IconBtn = styled.button`
  border: 1px solid #2c2b26; background: transparent; color: ${BONE}; cursor: pointer; width: 34px; height: 34px;
  font-size: 16px; font-weight: 800; line-height: 1; display: grid; place-items: center;
  &:hover{ border-color: ${ACID}; color: ${ACID}; }
`;
const MashovBtn = styled(IconBtn)`
  color: ${ACID}; border-color: #3a4a1a;
  &:hover{ background: rgba(215,255,62,.1); }
  &:disabled{ opacity: .55; cursor: progress; }
`;
const MailFrom = styled.div`font-size: 13px; font-weight: 600; color: #8a8578; margin-top: 3px;`;
const BellBtn = styled(IconBtn)`
  color: ${(p) => (p.$on ? ACID : BONE)};
  border-color: ${(p) => (p.$on ? "#3a4a1a" : "#2c2b26")};
  ${(p) => (p.$on ? "background: rgba(215,255,62,.1);" : "")}
`;
const MiniSpin = styled.div`
  width: 18px; height: 18px; border: 2px solid #2c2b26; border-top-color: currentColor; border-radius: 50%;
  animation: ${spin} .7s linear infinite;
`;
const MashovToast = styled.div`
  position: fixed; top: 12px; left: 50%; transform: translateX(-50%); z-index: 95; max-width: 92vw; text-align: center;
  background: ${(p) => (p.$ok ? "#12351f" : "#3a1620")}; border: 1px solid ${(p) => (p.$ok ? ACID : "#FF6478")};
  color: ${(p) => (p.$ok ? ACID : "#FF6478")}; font-weight: 800; font-size: 14px; padding: 10px 18px; direction: rtl;
  box-shadow: 0 8px 24px rgba(0,0,0,.5);
`;

/* collections */
const Collections = styled.div`
  max-width: 1180px; margin: 0 auto; padding: 12px 18px 4px; display: flex; align-items: center;
  justify-content: space-between; gap: 14px; flex-wrap: wrap;
`;
const CollScroll = styled.div`
  display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px; flex: 1 1 260px; min-width: 240px;
  scrollbar-width: none; &::-webkit-scrollbar{ display: none; }
`;
const Chip = styled.button`
  flex: 0 0 auto; border: 1px solid ${(p) => (p.$on ? ACID : "#2c2b26")};
  background: ${(p) => (p.$on ? ACID : "transparent")}; color: ${(p) => (p.$on ? INK : BONE)};
  cursor: pointer; font-family: inherit; font-size: 15px; font-weight: 800; padding: 9px 18px; white-space: nowrap;
  &:hover{ border-color: ${ACID}; }
`;
const ChipAdd = styled(Chip)`color: ${ACID}; border-style: dashed; background: transparent;`;
const ChipEdit = styled.span`margin-inline-start: 7px; font-size: 12px; opacity: .7;`;
const CamIcon = styled.img`height: 1.5em; width: auto; margin-inline-end: 7px; vertical-align: middle;`;

/* nav bar */
const NavBar = styled.div`
  max-width: 1180px; margin: 0 auto; padding: 8px 18px; display: flex; align-items: center;
  justify-content: center; gap: 12px; flex-wrap: wrap;
`;
const NavGroup = styled.div`display: flex; align-items: center; gap: 8px;`;
const NavArrow = styled.button`
  border: 1px solid #2c2b26; background: transparent; color: ${BONE}; cursor: pointer; width: 38px; height: 38px;
  font-size: 22px; font-weight: 900; line-height: 1; display: grid; place-items: center;
  &:hover{ border-color: ${ACID}; color: ${ACID}; }
`;
const RangeLabel = styled.button`
  border: none; background: transparent; color: ${BONE}; cursor: pointer; font-family: inherit; font-size: 17px;
  font-weight: 900; min-width: 150px; text-align: center; letter-spacing: -.3px;
`;
const SectionBar = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; border: 1px solid #2c2b26; margin: 2px 0 6px;
`;
const SectionBtn = styled.button`
  font-family: inherit; cursor: pointer; padding: 12px 10px; font-size: 16px; font-weight: 900; letter-spacing: -.3px;
  border: none; background: ${(p) => (p.$on ? ACID : "transparent")}; color: ${(p) => (p.$on ? INK : BONE)};
  &:not(:last-child){ border-inline-end: 1px solid #2c2b26; }
`;
const Segmented = styled.div`display: inline-flex; border: 1px solid #2c2b26; flex: 0 0 auto;`;
const Seg = styled.button`
  border: none; cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 800; padding: 9px 18px;
  background: ${(p) => (p.$on ? ACID : "transparent")}; color: ${(p) => (p.$on ? INK : "#a8a293")};
  & + &{ border-inline-start: 1px solid #2c2b26; }
  &:hover{ color: ${(p) => (p.$on ? INK : ACID)}; }
`;

/* install */
const InstallBar = styled.button`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; width: calc(100% - 36px);
  max-width: 1144px; margin: 8px auto 0; cursor: pointer; border: 1px solid ${ACID}; background: rgba(215,255,62,.06);
  color: ${BONE}; font-family: inherit; padding: 12px 16px; text-align: right;
  span{ font-weight: 900; font-size: 15px; } em{ font-style: normal; font-weight: 600; font-size: 12.5px; color: #a8a293; }
  &:hover{ background: rgba(215,255,62,.12); }
`;

/* week grid */
const GridWrap = styled.div`max-width: 1180px; margin: 8px auto 0; padding: 6px 18px 40px; overflow-x: auto;`;
const MWkSlot = styled.span`font-size: 13px; font-weight: 900; color: ${ACID}; direction: ltr;`;
const MWkCell = styled.div`
  border-left: 1px solid #201f1b; border-bottom: 1px solid #201f1b; min-height: 72px;
  background: #0f0e0b; display: flex; flex-direction: column; justify-content: center; text-align: center;
`;
const MWkItem = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center; text-align: center;
  padding: 6px 7px; font-size: 13px; font-weight: 800; line-height: 1.2; word-break: break-word;
  &:hover{ filter: brightness(1.08); }
`;
const Grid = styled.div`
  display: grid; grid-template-columns: 88px repeat(6, minmax(116px, 1fr));
  border-top: 1px solid #201f1b; border-right: 1px solid #201f1b;
`;
const cellBase = `border-left: 1px solid #201f1b; border-bottom: 1px solid #201f1b;`;
const Corner = styled.div`
  ${cellBase}; background: #141310; display: grid; place-items: center; font-size: 11px; font-weight: 800;
  letter-spacing: 2px; color: #6f6a5f; text-transform: uppercase;
`;
const HeadCell = styled.div`
  ${cellBase}; background: ${(p) => (p.$today ? "#1c1e10" : "#141310")}; padding: 10px 6px; text-align: center;
  border-bottom: ${(p) => (p.$today ? `2px solid ${ACID}` : "1px solid #201f1b")};
  cursor: pointer;
  &:hover{ background: #1e1c12; }
`;
const Hd = styled.div`font-size: 15px; font-weight: 900; color: ${BONE};`;
const HdDate = styled.div`font-size: 11px; font-weight: 700; color: #6f6a5f; margin-top: 2px; direction: ltr;`;
const HdHol = styled.div`
  font-size: 10.5px; font-weight: 800; color: ${ACID}; margin-top: 4px; line-height: 1.2;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const HdEv = styled.div`font-size: 10.5px; font-weight: 800; color: #ff9db0; margin-top: 3px;`;
const RowLabel = styled.div`
  ${cellBase}; background: #121109; display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 10px 4px; gap: 2px; cursor: pointer;
  &:hover{ background: #1a1810; }
`;
const RowNum = styled.div`font-size: 19px; font-weight: 900; color: ${ACID}; line-height: 1;`;
const RowTime = styled.div`
  display: flex; flex-direction: column; align-items: center; line-height: 1.25;
  font-size: 12px; font-weight: 700; color: #8a8578; direction: ltr;
`;
const GCell = styled.button`
  ${cellBase}; min-height: 72px; cursor: pointer; border-top: none; border-right: none;
  background: ${(p) => (p.$bg ? p.$bg : "transparent")}; font-family: inherit; padding: 8px; display: flex;
  align-items: center; justify-content: center; text-align: center; transition: background .12s;
  &:hover{ background: ${(p) => (p.$bg ? p.$bg : "#151410")}; }
`;
const GText = styled.span`font-size: 15px; font-weight: 800; line-height: 1.25; word-break: break-word;`;
const GTeacher = styled.span`display: block; margin-top: 3px; font-size: 11px; font-weight: 600; opacity: .72; word-break: break-word;`;
const Plus = styled.span`font-size: 18px; color: #34332d; font-weight: 700;`;

/* day view */
const DayWrap = styled.div`max-width: 640px; margin: 6px auto 0; padding: 0 16px 44px; animation: ${fade} .2s ease;`;
const DayHead = styled.div`
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding: 10px 2px 14px;
  border-bottom: 2px solid ${(p) => (p.$today ? ACID : "#201f1b")};
`;
const DayName = styled.div`font-size: 34px; font-weight: 900; letter-spacing: -1px; display: flex; align-items: center; gap: 10px;`;
const TodayDot = styled.span`font-size: 12px; font-weight: 800; color: ${INK}; background: ${ACID}; padding: 3px 9px;`;
const DayDate = styled.div`font-size: 15px; font-weight: 800; color: #8a8578; direction: ltr;`;
const HolBanner = styled.div`
  display: flex; align-items: center; gap: 10px; margin: 14px 0 4px; padding: 12px 14px;
  border: 1px solid ${ACID}; background: rgba(215,255,62,.07);
  span{ font-size: 22px; } b{ display: block; font-size: 16px; font-weight: 900; color: ${ACID}; }
`;
const RainBanner = styled.div`
  display: flex; align-items: center; gap: 10px; margin: 14px 0 4px; padding: 12px 14px;
  border: 1px solid #5aa0ff; background: rgba(90,160,255,.10);
  span{ font-size: 22px; } b{ display: block; font-size: 16px; font-weight: 900; color: #5aa0ff; }
`;
const SportBanner = styled.div`
  display: flex; align-items: center; gap: 10px; margin: 14px 0 4px; padding: 12px 14px;
  border: 1px solid #ff9f43; background: rgba(255,159,67,.10);
  span{ font-size: 22px; } b{ display: block; font-size: 16px; font-weight: 900; color: #ff9f43; }
`;
const EventsBox = styled.div`display: flex; flex-direction: column; gap: 7px; margin: 12px 0 4px;`;
const EventRow = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid #2c2b26;
  border-inline-start: 3px solid ${(p) => (p.$exam ? "#FF6478" : "#5aa0ff")}; background: #0f0e0b;
`;
const EvTag = styled.span`
  flex: 0 0 auto; font-size: 11px; font-weight: 900; padding: 3px 9px; color: ${INK};
  background: ${(p) => (p.$exam ? "#FF6478" : "#5aa0ff")};
`;
const EvText = styled.span`font-size: 15px; font-weight: 700; color: ${BONE}; line-height: 1.4;`;
const RestCard = styled.div`
  margin-top: 20px; padding: 44px 20px; text-align: center; border: 1px dashed #2c2b26; background: #0f0e0b;
`;
const RestEmoji = styled.div`font-size: 44px;`;
const RestText = styled.div`font-size: 18px; font-weight: 800; color: #8a8578; margin-top: 10px;`;
const RestHint = styled.div`font-size: 13px; font-weight: 700; color: #4a473f; margin-top: 8px;`;
const MeWrap = styled.div`display: flex; flex-direction: column; gap: 12px; margin-top: 12px; padding: 0 16px 44px;`;
const MeToolbar = styled.div`display: flex; justify-content: flex-start; padding: 12px 16px 2px;`;
const MeList = styled.div`display: flex; flex-direction: column; gap: 8px;`;
const LunchImg = styled.img`
  flex: 0 0 auto; margin-inline-start: auto; height: 40px; width: auto; mix-blend-mode: multiply;
`;
const VideoBtn = styled.button`
  flex: 0 0 auto; margin-inline-start: auto; white-space: nowrap; cursor: pointer; font-family: inherit;
  font-size: 12px; font-weight: 900; padding: 7px 11px; border: none; color: #fff; background: #2C6BFF;
  &:hover{ filter: brightness(1.12); }
`;
const WorkIcon = styled.span`font-size: 18px; line-height: 1; margin-inline-start: 4px;`;
const MeEmpty = styled.div`
  padding: 20px; text-align: center; border: 1px dashed #2c2b26; background: #0f0e0b;
  font-size: 14px; font-weight: 700; color: #8a8578;
`;
const MeConnectBtn = styled.button`
  font-family: inherit; cursor: pointer; padding: 14px 18px; font-size: 16px; font-weight: 900;
  border: none; background: ${ACID}; color: ${INK}; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  &:disabled{ opacity: .6; cursor: progress; }
`;
const DayList = styled.div`margin-top: 14px; display: flex; flex-direction: column; gap: 8px;`;
const DayRow = styled.div`
  display: grid; grid-template-columns: 72px 1fr; gap: 10px; align-items: stretch;
  font-family: inherit; padding: 0; animation: ${slideLeft} .32s cubic-bezier(.22,1,.36,1) both;
`;
const DayRowTime = styled.div`
  background: #121109; border: 1px solid #201f1b; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 2px; padding: 8px 4px; cursor: pointer;
  &:hover{ border-color: ${ACID}; }
`;
const DayRowBody = styled.div`
  border: 1px solid ${(p) => (p.$bg ? "transparent" : "#201f1b")}; background: ${(p) => (p.$bg ? p.$bg : "#0f0e0b")};
  min-height: 62px; display: flex; align-items: center; padding: 12px 16px; text-align: right; cursor: pointer;
  &:hover{ filter: brightness(1.08); }
`;
const DayRowText = styled.div`font-size: 17px; font-weight: 800; line-height: 1.3; word-break: break-word;`;
const DayRowTeacher = styled.span`display: block; margin-top: 3px; font-size: 12px; font-weight: 600; opacity: .72;`;
const DayRowEmpty = styled.div`font-size: 14px; font-weight: 700; color: #4a473f;`;

/* break strip */
const BreakBar = styled.div`
  position: relative; height: 34px; margin: 1px 0; overflow: hidden; display: flex; align-items: center;
  justify-content: center; border: 1px dashed #26251f; background: linear-gradient(90deg, #0e0d09, #131109, #0e0d09);
`;
const BreakLabel = styled.div`
  display: flex; align-items: center; gap: 10px; font-size: 12px; font-weight: 800; letter-spacing: .5px;
  color: #8a8578; padding: 0 14px;
`;

/* month view */
const MonthWrap = styled.div`max-width: 900px; margin: 8px auto 0; padding: 6px 14px 40px; animation: ${fade} .2s ease;`;
const MonthHead = styled.div`display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; margin-bottom: 6px;`;
const MHd = styled.div`
  text-align: center; font-size: 13px; font-weight: 800; padding: 6px 0;
  color: ${(p) => (p.$weekend ? "#5a564d" : "#a8a293")};
`;
const MonthGrid = styled.div`display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px;`;
const MCell = styled.button`
  aspect-ratio: 1 / 1; min-height: 52px; cursor: pointer; font-family: inherit; text-align: right; position: relative;
  border: 1px solid ${(p) => (p.$today ? ACID : "#201f1b")};
  background: ${(p) => (p.$today ? "rgba(215,255,62,.08)" : p.$weekend ? "#0c0b08" : "#121109")};
  opacity: ${(p) => (p.$in ? 1 : 0.32)}; padding: 6px; display: flex; flex-direction: column; gap: 2px; overflow: hidden;
  &:hover{ border-color: ${ACID}; }
`;
const MNum = styled.div`
  font-size: 15px; font-weight: 900; align-self: flex-end;
  color: ${(p) => (p.$today ? ACID : BONE)};
`;
const MHol = styled.div`
  font-size: 10px; font-weight: 800; color: ${ACID}; line-height: 1.15; text-align: right;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; direction: rtl;
`;
const MDot = styled.span`width: 6px; height: 6px; background: #2C6BFF; border-radius: 50%; align-self: flex-end; margin-top: auto;`;
const MEv = styled.div`
  font-size: 9.5px; font-weight: 800; line-height: 1.15; margin-top: 1px;
  color: ${(p) => (p.$exam ? "#ff6478" : "#5aa0ff")};
`;
const MonthLegend = styled.div`
  display: flex; gap: 20px; justify-content: center; margin-top: 16px; font-size: 12px; font-weight: 700; color: #8a8578;
`;
const Lg = styled.div`display: inline-flex; align-items: center; gap: 6px;`;

/* gear menu + about */
const GearWrap = styled.div`position: relative;`;
const GearBtn = styled(IconBtn)`font-size: 25px;`;
const MenuBackdrop = styled.div`position: fixed; inset: 0; z-index: 40;`;
const GearMenu = styled.div`
  position: absolute; top: calc(100% + 6px); left: 0; right: auto; z-index: 41; min-width: 180px;
  background: #16150f; border: 1px solid #2c2b26; padding: 6px; display: flex; flex-direction: column; gap: 2px;
  box-shadow: 0 12px 30px rgba(0,0,0,.5);
`;
const GearItem = styled.button`
  border: none; background: transparent; cursor: pointer; font-family: inherit; font-size: 15px;
  font-weight: 800; color: ${BONE}; padding: 11px 12px; white-space: nowrap;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  span { flex: 1; text-align: right; }
  svg { flex: 0 0 auto; }
  &:hover{ background: #201f18; color: ${ACID}; }
`;
const GearDivider = styled.div`height: 1px; background: #201f1b; margin: 5px 4px;`;
const GearFooter = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-top: 4px; padding: 6px 8px 2px; border-top: 1px solid #201f1b;
`;
/* list view (homework / behavior) */
const ListWrap = styled.div`
  max-width: 640px; margin: 10px auto 0; padding: 0 16px 44px; display: flex; flex-direction: column; gap: 8px;
  animation: ${fade} .2s ease;
`;
const ListEmpty = styled.div`
  max-width: 460px; margin: 40px auto; text-align: center; font-size: 16px; font-weight: 700; color: #8a8578;
  line-height: 1.7; padding: 0 20px;
`;
const ListRow = styled.div`
  display: grid; grid-template-columns: 56px 1fr auto; gap: 10px; align-items: center; padding: 12px 14px;
  border: 1px solid #201f1b; border-inline-start: 3px solid ${(p) => p.$accent || "#5aa0ff"}; background: #0f0e0b;
  animation: ${slideLeft} .32s cubic-bezier(.22,1,.36,1) both;
`;
const ListAction = styled.button`
  flex: 0 0 auto; white-space: nowrap; cursor: pointer; font-family: inherit; font-size: 12px; font-weight: 800;
  padding: 7px 11px; border: 1px solid ${(p) => (p.$wa ? "#2e7d46" : "#3a4a1a")};
  color: ${(p) => (p.$wa ? "#57c785" : ACID)}; background: ${(p) => (p.$wa ? "rgba(87,199,133,.1)" : "rgba(215,255,62,.08)")};
  &:hover{ filter: brightness(1.2); }
`;
const ListDate = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 1px;
  b{ font-size: 15px; font-weight: 900; color: ${ACID}; } small{ font-size: 11px; color: #8a8578; direction: ltr; }
`;
const ListBody = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;
const ListText = styled.div`font-size: 16px; font-weight: 700; color: ${BONE}; line-height: 1.4;`;
const RefreshBtn = styled.button`
  border: 1px solid #2c2b26; background: transparent; color: #8a8578; cursor: pointer;
  width: 32px; height: 32px; display: grid; place-items: center; flex: 0 0 auto;
  &:hover{ border-color: ${ACID}; color: ${ACID}; }
  &:active svg{ transform: rotate(180deg); transition: transform .3s; }
`;
const GearVersion = styled.div`
  font-size: 11px; font-weight: 700; letter-spacing: .5px; color: #5a564d; direction: rtl;
`;
const AboutText = styled.div`
  font-size: 16px; font-weight: 600; color: #cbc6ba; line-height: 1.8; b{ color: ${BONE}; font-weight: 900; }
`;
/* reminders sheet */
const RemNote = styled.div`
  font-size: 13px; font-weight: 700; color: #d9c98a; background: rgba(215,255,62,.06);
  border: 1px solid #4a4620; padding: 10px 12px; margin-bottom: 14px; line-height: 1.6;
`;
/* Mashov school search */
const SchoolWrap = styled.div`position: relative;`;
const SchoolResults = styled.div`
  position: absolute; top: calc(100% + 4px); inset-inline: 0; z-index: 5; max-height: 240px; overflow-y: auto;
  background: #0e0d09; border: 1px solid #2c2b26; box-shadow: 0 12px 30px rgba(0,0,0,.5);
`;
const SchoolItem = styled.button`
  display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; text-align: right;
  border: none; border-bottom: 1px solid #1c1b16; background: transparent; cursor: pointer; font-family: inherit;
  padding: 10px 12px; color: ${BONE};
  span{ font-size: 14px; font-weight: 700; } small{ font-size: 11px; color: #8a8578; direction: ltr; }
  &:hover{ background: #1a1810; color: ${ACID}; }
`;
const SchoolHint = styled.div`padding: 10px 12px; font-size: 13px; font-weight: 700; color: #8a8578;`;
const Picked = styled.div`margin-top: 8px; font-size: 13px; font-weight: 800; color: ${ACID};`;
const RemRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; cursor: pointer;
  padding: 12px 2px; border-bottom: 1px solid #201f1b;
`;
const RemLabel = styled.div`font-size: 17px; font-weight: 800; color: ${BONE};`;
const RemHint = styled.span`font-size: 12px; font-weight: 600; color: #8a8578; margin-inline-start: 6px;`;
const Toggle = styled.div`
  width: 52px; height: 30px; border-radius: 999px; flex: 0 0 auto; position: relative; transition: background .15s;
  background: ${(p) => (p.$on ? ACID : "#2c2b26")};
`;
const ToggleKnob = styled.div`
  position: absolute; top: 3px; width: 24px; height: 24px; border-radius: 50%; background: ${INK}; transition: left .15s, right .15s;
  ${(p) => (p.$on ? "left: 3px;" : "right: 3px;")}
`;
const RemSection = styled.div`margin-top: 14px; transition: opacity .15s;`;
const RemSub = styled.div`font-size: 13px; font-weight: 800; letter-spacing: .5px; color: #8a8578; margin: 14px 0 8px;`;
const PassHelp = styled.a`font-size: 12px; font-weight: 700; letter-spacing: 0; color: ${ACID}; text-decoration: underline; text-underline-offset: 2px; &:hover{ opacity: .8; }`;
const ChipRow = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const PickChip = styled.button`
  cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 800; padding: 9px 15px;
  border: 1px solid ${(p) => (p.$on ? ACID : "#2c2b26")}; background: ${(p) => (p.$on ? ACID : "transparent")};
  color: ${(p) => (p.$on ? INK : BONE)}; &:hover{ border-color: ${ACID}; }
`;
const AboutMail = styled.a`
  display: inline-block; margin-top: 16px; direction: ltr; font-size: 16px; font-weight: 800; color: ${ACID};
  text-decoration: none; border-bottom: 1px solid ${ACID}; padding-bottom: 2px;
`;

/* overlay / sheet */
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 80; background: rgba(0,0,0,.66); backdrop-filter: blur(4px);
  display: flex; align-items: flex-end; justify-content: center; padding: 0;
  @media (min-width: 640px){ align-items: center; padding: 20px; }
`;
const ImportOverlay = styled.div`
  position: fixed; inset: 0; z-index: 90; background: rgba(0,0,0,.8); backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center; padding: 20px;
`;
const ImportBox = styled.div`display: flex; flex-direction: column; align-items: center; gap: 14px; text-align: center;`;
const ImportSpin = styled.div`
  width: 54px; height: 54px; border: 4px solid #2c2b26; border-top-color: ${ACID}; border-radius: 50%;
  animation: ${spin} .8s linear infinite;
`;
const ImportText = styled.div`font-size: 19px; font-weight: 900; color: ${BONE};`;
const ImportSub = styled.div`font-size: 14px; font-weight: 600; color: #8a8578;`;
const SheetBox = styled.div`
  width: 100%; max-width: 520px; background: #16150f; border: 1px solid #2c2b26; padding: 20px 20px 26px; direction: rtl;
  animation: ${rise} .22s ease; max-height: 88vh; overflow-y: auto;
`;
const SheetHead = styled.div`display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px;`;
const SheetTitle = styled.div`font-size: 22px; font-weight: 900; color: ${BONE};`;
const SheetSub = styled.div`font-size: 13px; font-weight: 700; color: #8a8578; margin-top: 3px; direction: ltr; text-align: right;`;
const Field = styled.input`
  width: 100%; font-family: inherit; font-size: 18px; font-weight: 700; color: ${BONE}; background: #0e0d09;
  border: 1px solid #2c2b26; padding: 14px; outline: none; text-align: right;
  &:focus{ border-color: ${ACID}; } &::placeholder{ color: #55524a; font-weight: 600; }
`;
const SwatchLabel = styled.div`font-size: 12px; font-weight: 800; letter-spacing: 1px; color: #8a8578; margin: 18px 0 8px;`;
const Swatches = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const Swatch = styled.button`
  width: 40px; height: 40px; cursor: pointer; border: 2px solid ${(p) => (p.$on ? BONE : "transparent")};
  background: ${(p) => p.$c}; outline: ${(p) => (p.$on ? `2px solid ${p.$c}` : "none")}; outline-offset: 1px;
`;
const SwatchNone = styled.button`
  width: 40px; height: 40px; cursor: pointer; background: #0e0d09; border: 1px solid ${(p) => (p.$on ? ACID : "#2c2b26")};
  color: #8a8578; font-weight: 800; font-size: 15px;
`;
const SheetActions = styled.div`display: flex; align-items: stretch; gap: 10px; margin-top: 22px;`;
const BigBtn = styled.button`
  flex: 1; border: none; cursor: pointer; font-family: inherit; font-size: 17px; font-weight: 900; color: ${INK};
  background: ${ACID}; padding: 14px; transition: transform .08s, filter .15s;
  &:hover:not(:disabled){ filter: brightness(1.05); } &:active{ transform: translateY(2px); } &:disabled{ opacity:.45; cursor:not-allowed; }
`;
const GhostBtn = styled.button`
  flex: 1; cursor: pointer; font-family: inherit; font-size: 15px; font-weight: 800; background: transparent; padding: 14px;
  display: inline-flex; align-items: center; justify-content: center;
  color: ${(p) => (p.$danger ? "#FF6478" : BONE)}; border: 1px solid ${(p) => (p.$danger ? "#5a2630" : "#2c2b26")};
  &:hover{ border-color: ${(p) => (p.$danger ? "#FF6478" : ACID)}; }
`;
const WarnNote = styled.div`
  margin-top: 10px; font-size: 12.5px; font-weight: 700; line-height: 1.5; color: #d9c98a;
  background: rgba(215,255,62,.05); border: 1px solid #4a4620; padding: 9px 11px; text-align: right;
`;
const ConnectedNote = styled.div`
  margin-top: 10px; font-size: 13px; font-weight: 800; line-height: 1.5; color: ${ACID};
  background: rgba(215,255,62,.08); border: 1px solid #3a4a1a; padding: 10px 12px; text-align: center;
`;

/* mini button (rows editor) */
const MiniBtn = styled.button`
  border: 1px solid ${(p) => (p.$danger ? "#5a2630" : "#2c2b26")}; cursor: pointer; font-family: inherit; font-size: 13px;
  font-weight: 800; padding: 7px 12px; background: transparent; color: ${(p) => (p.$danger ? "#FF6478" : BONE)};
  &:hover:not(:disabled){ border-color: ${(p) => (p.$danger ? "#FF6478" : ACID)}; } &:disabled{ opacity:.35; cursor:not-allowed; }
`;

/* rows editor */
const RowsList = styled.div`display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px;`;
const REditRow = styled.div`display: flex; align-items: center; gap: 6px;`;
const REorder = styled.div`display: flex; flex-direction: column; gap: 3px;`;
const RTitleIn = styled.input`
  width: 52px; font-family: inherit; font-size: 15px; font-weight: 800; text-align: center; color: ${ACID};
  background: #0e0d09; border: 1px solid #2c2b26; padding: 10px 4px; outline: none; &:focus{ border-color: ${ACID}; }
`;
const RTimeIn = styled.input`
  flex: 1; min-width: 0; font-family: inherit; font-size: 15px; font-weight: 700; direction: ltr; text-align: center;
  color: ${BONE}; background: #0e0d09; border: 1px solid #2c2b26; padding: 10px; outline: none;
  &:focus{ border-color: ${ACID}; } &::placeholder{ color: #55524a; }
`;

/* weather sheet */
const WxLoading = styled.div`padding: 24px; text-align: center; font-weight: 700; color: #8a8578;`;
const NewsList = styled.div`display: flex; flex-direction: column; margin-top: 6px;`;
const NewsItem = styled.a`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 13px 4px;
  border-bottom: 1px solid #201f1b; text-decoration: none; color: ${BONE};
  &:hover{ background: rgba(215,255,62,.05); }
  &:last-child{ border-bottom: none; }
`;
const NewsText = styled.span`font-size: 15px; font-weight: 700; line-height: 1.4;`;
const NewsTime = styled.span`flex: 0 0 auto; font-size: 12px; font-weight: 700; color: #8a8578; direction: ltr;`;
const WxNow = styled.div`display: flex; align-items: center; gap: 16px; padding: 8px 4px 18px; border-bottom: 1px solid #2c2b26;`;
const WxNowEmoji = styled.div`font-size: 54px; line-height: 1;`;
const WxNowTemp = styled.div`font-size: 44px; font-weight: 900; color: ${BONE}; line-height: 1;`;
const WxNowLabel = styled.div`font-size: 15px; font-weight: 700; color: #a8a293; margin-top: 4px;`;
const WxList = styled.div`display: flex; flex-direction: column; margin-top: 6px;`;
const WxRow = styled.div`
  display: grid; grid-template-columns: auto 34px 1fr auto; align-items: center; gap: 10px; padding: 11px 2px;
  border-bottom: 1px solid #201f1b;
`;
const WxDay = styled.div`font-size: 15px; font-weight: 800; color: ${BONE}; display: flex; align-items: baseline; gap: 7px;`;
const WxDate = styled.span`font-size: 11px; font-weight: 700; color: #6f6a5f; direction: ltr;`;
const WxEmoji = styled.div`font-size: 22px; text-align: center;`;
const WxHol = styled.div`font-size: 12px; font-weight: 800; color: ${ACID}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const WxTemps = styled.div`display: flex; align-items: baseline; gap: 8px;`;
const WxMax = styled.span`font-size: 16px; font-weight: 900; color: ${BONE};`;
const WxMin = styled.span`font-size: 14px; font-weight: 700; color: #6f6a5f;`;

/* iOS hint */
const IosSteps = styled.ol`
  margin: 0; padding: 0 20px; display: flex; flex-direction: column; gap: 14px; font-size: 16px; font-weight: 600;
  line-height: 1.6; color: #cbc6ba; b{ color: ${BONE}; }
`;
const IosGlyph = styled.span`color: ${ACID}; font-weight: 900;`;
